import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardMedia,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, addDays, parseISO, isMonday } from "date-fns";
import PhotoUploadWithDrawing from "../../components/siteCheck/PhotoUploadWithDrawing";
import useFormPersistence from "../../hooks/useFormPersistence";

// Custom Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder, height = "161px" }) => {
  const [isFocused, setIsFocused] = useState(false);
  const editorRef = React.useRef(null);

  const handleTextChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const handleFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleAlignment = (alignment) => {
    const alignmentMap = {
      left: "justifyLeft",
      center: "justifyCenter",
      right: "justifyRight",
      justify: "justifyFull",
    };
    document.execCommand(alignmentMap[alignment]);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleList = (listType) => {
    const command =
      listType === "unordered" ? "insertUnorderedList" : "insertOrderedList";
    document.execCommand(command);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleClearFormat = () => {
    // Select all text first
    document.execCommand("selectAll");
    // Remove all formatting
    document.execCommand("removeFormat");
    // Clear any remaining styles
    document.execCommand("unlink");
    document.execCommand("formatBlock", false, "p");
    handleTextChange();
    editorRef.current.focus();
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    handleTextChange();
  };

  // Set initial content
  React.useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1,
          p: 1,
          border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
          borderRadius: "8px 8px 0 0",
          backgroundColor: "#f5f5f5",
          flexWrap: "wrap",
        }}
      >
        {/* Bold, Italic, Underline */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleFormat("bold")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Bold"
          >
            <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>B</Typography>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleFormat("italic")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              fontStyle: "italic",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Italic"
          >
            I
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleFormat("underline")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              textDecoration: "underline",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Underline"
          >
            U
          </IconButton>
        </Box>

        {/* Alignment */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleAlignment("left")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Align Left"
          >
            <Icon name="alignLeft" size={16} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment("center")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Align Center"
          >
            <Icon name="alignCenter" size={16} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment("right")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Align Right"
          >
            <Icon name="alignRight" size={16} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment("justify")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Justify"
          >
            <Icon name="alignJustify" size={16} color={COLORS.TEXT} />
          </IconButton>
        </Box>

        {/* Lists */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleList("unordered")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Bulleted List"
          >
            <Icon name="listUnordered" size={16} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleList("ordered")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Numbered List"
          >
            <Icon name="listOrdered" size={16} color={COLORS.TEXT} />
          </IconButton>
        </Box>

        {/* Clear Formatting */}
        <Box display="flex" gap={0.5}>
          <Button
            size="small"
            onClick={handleClearFormat}
            sx={{
              height: 36,
              fontSize: 12,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              color: COLORS.PRIMARY,
              "&:hover": { backgroundColor: "#f8f8f8" },
            }}
          >
            Clear Format
          </Button>
          <Button
            size="small"
            onClick={() => {
              if (editorRef.current) {
                editorRef.current.innerHTML = "";
                onChange("");
              }
            }}
            sx={{
              height: 36,
              fontSize: 12,
              backgroundColor: "#fff",
              border: `1px solid ${COLORS.ERROR}`,
              color: COLORS.ERROR,
              "&:hover": { backgroundColor: `${COLORS.ERROR}15` },
            }}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {/* Editor Area */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleTextChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        sx={{
          border: `1px solid ${isFocused ? COLORS.PRIMARY : COLORS.LIGHT_SECONDARY}`,
          borderRadius: "0 0 8px 8px",
          minHeight: height,
          padding: "12px",
          fontFamily: FONT_FAMILY,
          fontSize: "14px",
          color: COLORS.PRIMARY,
          outline: "none",
          overflowY: "auto",
          backgroundColor: "#fff",
          "&:empty:before": {
            content: `"${placeholder || "Start typing here..."}"`,
            color: COLORS.LIGHT_SECONDARY,
          },
          "&:focus": {
            borderColor: COLORS.PRIMARY,
          },
          "& p": {
            margin: "0 0 8px 0",
            color: COLORS.PRIMARY,
          },
          "& ul, & ol": {
            margin: "0 0 8px 0",
            paddingLeft: "20px",
            color: COLORS.PRIMARY,
          },
          "& li": {
            color: COLORS.PRIMARY,
          },
        }}
      />
    </Box>
  );
};

// ================= FLEX HELPER COMPONENTS (for Planned vs Actual section) =================
const FlexRow = ({ children, header }) => (
  <Box
    display="flex"
    alignItems="stretch"
    sx={{
      py: header ? 1.2 : 1,
      fontWeight: header ? 600 : 400,
    }}
  >
    {children}
  </Box>
);

const FlexCell = ({
  children,
  flex,
  w,
  sx = {},
  borderBottom = "none",
  ...props
}) => (
  <Box
    flex={flex}
    width={w}
    px={1}
    sx={{
      ...TYPOGRAPHY.BODY_LARGE,
      borderBottom: borderBottom,
      wordWrap: "break-word",
      whiteSpace: "normal",
      overflowWrap: "break-word",
      lineHeight: 1.4,
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

const FlexRowInner = ({ children, header }) => (
  <Box
    display="flex"
    flex={1}
    alignItems="stretch"
    sx={{
      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
    }}
  >
    {children}
  </Box>
);

const FlexEnd = ({ children, mt }) => (
  <Box display="flex" justifyContent="flex-end" mt={mt}>
    {children}
  </Box>
);

export default function AddWMR() {
  const { projectId } = useParams();
  const { token, userData, isEngineer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const pendingWMRRef = useRef(null);

  // Get week data from navigation state
  const {
    weekStartDate: initialWeekStartDate,
    weekEndDate: initialWeekEndDate,
    projectName: propProjectName,
  } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");

  // Week date state - Initialize from props
  const [weekStartDate, setWeekStartDate] = useState(
    initialWeekStartDate ? new Date(initialWeekStartDate) : null,
  );
  const [weekEndDate, setWeekEndDate] = useState(
    initialWeekEndDate ? new Date(initialWeekEndDate) : null,
  );
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Current WMR ID for tracking existing WMR
  const [currentWMRId, setCurrentWMRId] = useState(null);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // WPR import data
  const [saving, setSaving] = useState(false);

  // Track original files to identify removed ones
  const [originalReportFiles, setOriginalReportFiles] = useState([]);
  const [originalPhotos, setOriginalPhotos] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [showWPRImport, setShowWPRImport] = useState(false);
  const [isWPRImported, setIsWPRImported] = useState(false);
  const [wprData, setWprData] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  // WMR data state with persistence
  const persistenceKey = `wmr-form-${projectId}-${weekStartDate ? format(weekStartDate, "yyyy-MM-dd") : "no-week"}`;
  const [wmrData, setWmrData, clearPersistence] = useFormPersistence(persistenceKey, {
    workExecuted: {
      rccWork: "",
      civilWorks: "",
      masonryFinishing: "",
      mepWorks: "",
      externalDevelopment: "",
    },
    workInProgress: {
      mergedProgress: [],
    },
    plannedVsActual: {
      monday: { entries: [] },
      tuesday: { entries: [] },
      wednesday: { entries: [] },
      thursday: { entries: [] },
      friday: { entries: [] },
      saturday: { entries: [] },
    },
    manpowerDeployment: {
      laborTypes: [
        { type: "Engineer", data: [0, 0, 0, 0, 0, 0] },
        { type: "Supervisors", data: [0, 0, 0, 0, 0, 0] },
        { type: "Unskilled Labour", data: [0, 0, 0, 0, 0, 0] },
        { type: "Carpenter", data: [0, 0, 0, 0, 0, 0] },
      ],
      dates: [],
    },
    machineryEquipment: [
      { equipment: "Excavator", quantity: "", hours: "", utilization: "-" },
      { equipment: "JCB", quantity: "", hours: "", utilization: "-" },
      {
        equipment: "Concrete Mixer",
        quantity: "",
        hours: "",
        utilization: "-",
      },
      { equipment: "Tower Crane", quantity: "", hours: "", utilization: "-" },
      { equipment: "Mobile Crane", quantity: "", hours: "", utilization: "-" },
      {
        equipment: "Concrete Pumping",
        quantity: "",
        hours: "",
        utilization: "-",
      },
      { equipment: "Vibrator", quantity: "", hours: "", utilization: "-" },
      { equipment: "Compactor", quantity: "", hours: "", utilization: "-" },
      {
        equipment: "Bar Bending Machine",
        quantity: "",
        hours: "",
        utilization: "-",
      },
      {
        equipment: "Bar Cutting Machine",
        quantity: "",
        hours: "",
        utilization: "-",
      },
      {
        equipment: "Welding Machine",
        quantity: "",
        hours: "",
        utilization: "-",
      },
      {
        equipment: "Generator / DG Set",
        quantity: "",
        hours: "",
        utilization: "-",
      },
    ],
    materialStatus: [
      {
        description: "Cement (OPC / PPC)",
        received: "",
        consumed: "",
        balance: "",
      },
      {
        description: "Steel / TMT Bars",
        received: "",
        consumed: "",
        balance: "",
      },
      {
        description: "Coarse Aggregate (20mm)",
        received: "",
        consumed: "",
        balance: "",
      },
      {
        description: "Coarse Aggregate (10mm)",
        received: "",
        consumed: "",
        balance: "",
      },
      {
        description: "Fine Aggregate / Sand",
        received: "",
        consumed: "",
        balance: "",
      },
      { description: "Bricks", received: "", consumed: "", balance: "" },
      {
        description: "Ready Mix Concrete",
        received: "",
        consumed: "",
        balance: "",
      },
      {
        description: "Concrete Blocks",
        received: "",
        consumed: "",
        balance: "",
      },
      {
        description: "Plywood / Shuttering",
        received: "",
        consumed: "",
        balance: "",
      },
      { description: "Binding Wire", received: "", consumed: "", balance: "" },
      { description: "Nails", received: "", consumed: "", balance: "" },
      {
        description: "MS Channels / Angles",
        received: "",
        consumed: "",
        balance: "",
      },
    ],
    qualityControl: [
      {
        inspection: "What Inspection we Carried out",
        test: "",
        observation: "Observations in Text Format",
      },
      {
        inspection: "Inspection 1",
        test: "",
        observation: "Attached file title from Report",
      },
      { inspection: "Inspection seconds", test: "", observation: "" },
    ],
    safetyHSE: {
      safetyCompliance: "",
      toolboxTalks: "",
      accidentsNearMisses: "",
    },
    issuesConstraints: {
      technical: "",
      material: "",
      manpower: "",
      designApproval: "",
    },
    instructionsDecisions: [],
    reports: [],
    decisionDiscussion: "",
    planNextWeek: {
      targetActivities: "",
      resourcePlanning: "",
    },
    photos: [],
    projectLayoutImagePreview: null,
    projectLayoutImageName: "",
  });

  // Form states for dynamic sections
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showMachineEntry, setShowMachineEntry] = useState(false);
  const [showMaterialEntry, setShowMaterialEntry] = useState(false);
  const [showQualityEntry, setShowQualityEntry] = useState(false);
  const [openReports, setOpenReports] = useState(true);
  const [openDay, setOpenDay] = useState(null);

  // Edit state for tracking which entry is being edited
  const [editingEntry, setEditingEntry] = useState({
    section: null, // 'progress', 'machine', 'material', 'quality', 'plannedActual'
    index: null, // For simple arrays
    dayIndex: null, // For planned vs actual section (which day)
    entryIndex: null, // For planned vs actual section (which entry in the day)
    data: null, // The entry data being edited
  });
  
  const plannedActualPersistenceKey = `wmr-planned-actual-${projectId}-${openDay ?? 'no-day'}-${editingEntry?.section === 'plannedActual' ? editingEntry.data?._id || editingEntry.entryIndex : 'new'}`;
  const [newEntry, setNewEntry, clearNewEntryPersistence] = useFormPersistence(plannedActualPersistenceKey, {
    contractor: "",
    activity: "",
    status: "OPEN",
    variance: "",
    expectedCompletionDate: null,
  });
  const [newProgressEntry, setNewProgressEntry] = useState({
    activity: "",
    location: "",
    status: "",
    remark: "",
  });
  const [newMachine, setNewMachine] = useState({
    equipment: "",
    quantity: "",
    hours: "",
    utilization: "",
  });
  const [newMaterial, setNewMaterial] = useState({
    description: "",
    received: "",
    consumed: "",
    balance: "",
  });
  const [newQuality, setNewQuality] = useState({
    inspection: "",
    test: "",
    observation: "",
    status: "OPEN",
  });
  const [newVisitor, setNewVisitor] = useState({
    date: new Date(),
    visitorName: "",
    designation: "",
    otherDesignation: "",
    instruction: "",
    remark: "",
  });
  const [showVisitorEntry, setShowVisitorEntry] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState(null);
  const [showOtherConsultantInput, setShowOtherConsultantInput] =
    useState(false);
  const [uploadedReports, setUploadedReports] = useState([]);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [showPhotoUploadDialog, setShowPhotoUploadDialog] = useState(false);

  // Project Layout Image state
  const [projectLayoutImage, setProjectLayoutImage] = useState(null);
  const [projectLayoutImagePreview, setProjectLayoutImagePreview] =
    useState(null);

  // NEW STATE for Planned vs Actual section to work like WPR
  const [weekDaysData, setWeekDaysData] = useState([]);

  // State for tracking original week data before WPR import
  const [originalWeekDaysData, setOriginalWeekDaysData] = useState([]);

  // Add a ref to track the current week being processed
  const currentWeekRef = useRef(null);

  // Format date functions
  const formatDateForWeekHeader = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return format(d, "dd/MM/yy");
  };

  const formatDateForWeekColumn = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate();
    const suffix = getDaySuffix(day);
    const month = d.toLocaleString("default", { month: "short" });
    const year = d.getFullYear().toString().slice(-2);
    const dayName = d.toLocaleString("default", { weekday: "long" });

    return `${dayName} ${day}${suffix} ${month} '${year}`;
  };

  const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // Restore images from persistence on load
  useEffect(() => {
    if (wmrData.photos && wmrData.photos.length > 0 && uploadedPhotos.length === 0) {
      const restoredPhotos = wmrData.photos.map(photo => {
        if (photo.imageUrl && photo.imageUrl.startsWith('data:image')) {
          try {
            const base64Data = photo.imageUrl.split(',')[1];
            const binaryData = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(binaryData.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < binaryData.length; i++) {
              uint8Array[i] = binaryData.charCodeAt(i);
            }
            const blob = new Blob([uint8Array], { type: photo.type || 'image/png' });
            const file = new File([blob], photo.name || `photo_${Date.now()}.png`, { type: photo.type || 'image/png' });
            return { ...photo, file };
          } catch (e) {
            console.error("Error restoring photo from persistence:", e);
            return photo;
          }
        }
        return photo;
      });
      setUploadedPhotos(restoredPhotos);
    }

    if (wmrData.projectLayoutImagePreview && !projectLayoutImagePreview) {
      setProjectLayoutImagePreview(wmrData.projectLayoutImagePreview);
      if (wmrData.projectLayoutImagePreview.startsWith('data:image')) {
        try {
          const base64Data = wmrData.projectLayoutImagePreview.split(',')[1];
          const binaryData = atob(base64Data);
          const arrayBuffer = new ArrayBuffer(binaryData.length);
          const uint8Array = new Uint8Array(arrayBuffer);
          for (let i = 0; i < binaryData.length; i++) {
            uint8Array[i] = binaryData.charCodeAt(i);
          }
          const blob = new Blob([uint8Array], { type: 'image/png' });
          const file = new File([blob], wmrData.projectLayoutImageName || "layout.png", { type: 'image/png' });
          setProjectLayoutImage(file);
        } catch (e) {
          console.error("Error restoring layout image:", e);
        }
      }
    }
  }, [wmrData.photos, wmrData.projectLayoutImagePreview]);

  // Auto-show photo upload dialog if draft exists
  useEffect(() => {
    if (persistenceKey && !showPhotoUploadDialog) {
      const photoDraftKey = `wmr-photo-draft-${persistenceKey}`;
      const draft = localStorage.getItem(photoDraftKey);
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          if (parsed && (parsed.title || parsed.preview || (parsed.shapes && parsed.shapes.length > 0))) {
            setShowPhotoUploadDialog(true);
          }
        } catch (e) {}
      }
    }
  }, [persistenceKey]);

  // Generate week days data for Planned vs Actual section
  const generateWeekDaysData = (startDate, endDate) => {
    if (!startDate || !endDate) return [];

    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const weekDataArray = [];

    const currentDate = new Date(startDate);

    for (let i = 0; i < 6; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);

      weekDataArray.push({
        day: days[i],
        date: date,
        formattedDate: formatDateForWeekColumn(date),
        entries: [],
      });
    }

    return weekDataArray;
  };

  // Auto-calculate balance for new material entry
  useEffect(() => {
    const received = parseFloat(newMaterial.received) || 0;
    const consumed = parseFloat(newMaterial.consumed) || 0;
    setNewMaterial((prev) => ({
      ...prev,
      balance: (received - consumed).toString(),
    }));
  }, [newMaterial.received, newMaterial.consumed]);

  // Fetch project data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // Fetch project details
        const projectResponse = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config,
        );

        if (projectResponse.data.project) {
          setProject(projectResponse.data.project);

          // Fetch companies for PMC dropdown
          const companiesResponse = await axios.get(
            `${url}/api/company/fetch`,
            config,
          );

          if (companiesResponse.data.data) {
            setCompanies(companiesResponse.data.data);
            // Select first company by default
            if (companiesResponse.data.data.length > 0) {
              setSelectedCompany(companiesResponse.data.data[0]._id);
            }
          }

          // If week dates are provided from props, initialize week data
          if (initialWeekStartDate && initialWeekEndDate) {
            const startDate = new Date(initialWeekStartDate);
            const endDate = new Date(initialWeekEndDate);
            setWeekStartDate(startDate);
            setWeekEndDate(endDate);
            initializeWeekData(startDate, endDate);

            // Fetch WMR data for the initial week
            setTimeout(() => {
              fetchWMRForWeek(startDate, endDate);
            }, 500);
          } else {
            setLoading(false);
          }
        } else {
          setError("Project not found");
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load project data. Please try again.");
        setLoading(false);
      }
    };

    if (projectId && token) {
      fetchData();
    }
  }, [projectId, token, initialWeekStartDate, initialWeekEndDate]);

  // Fetch WMR data when weekDaysData is initialized and week dates are set
  useEffect(() => {
    if (weekStartDate && weekEndDate && weekDaysData.length > 0) {
      const weekKey = `${format(weekStartDate, "yyyy-MM-dd")}-${format(weekEndDate, "yyyy-MM-dd")}`;

      // Only fetch if this is a different week than the one we just processed
      if (currentWeekRef.current !== weekKey) {
        console.log(
          "useEffect triggered: weekDaysData is ready, fetching WMR for week:",
          weekKey,
        );
        currentWeekRef.current = weekKey;

        // Add small delay to ensure all state is properly set
        setTimeout(() => {
          fetchWMRForWeek(weekStartDate, weekEndDate);
        }, 100);
      } else {
        console.log("Skipping fetch for already processed week:", weekKey);
      }
    }
  }, [weekStartDate, weekEndDate, weekDaysData.length]);

  // Initialize week data
  const initializeWeekData = (startDateParam, endDate) => {
    return new Promise((resolve) => {
      console.log(
        "initializeWeekData: Starting with dates:",
        startDateParam,
        endDate,
      );

      // Robust date generation to avoid timezone shifts
      const dates = [];
      let startDate;
      if (typeof startDateParam === "string") {
        const [year, month, day] = startDateParam
          .split("T")[0]
          .split("-")
          .map(Number);
        startDate = new Date(year, month - 1, day);
      } else {
        startDate = new Date(startDateParam);
      }

      for (let i = 0; i < 6; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push(format(date, "dd/MM/yy"));
      }

      console.log("initializeWeekData: Generated display dates:", dates);

      // Update manpower deployment dates
      setWmrData((prev) => ({
        ...prev,
        manpowerDeployment: {
          ...prev.manpowerDeployment,
          dates: dates,
        },
      }));

      // Initialize week days data for Planned vs Actual section
      const weekDays = generateWeekDaysData(startDate, endDate);
      console.log(
        "initializeWeekData: Generated weekDays:",
        weekDays.map((d) => ({
          day: d.day,
          date: format(d.date, "yyyy-MM-dd"),
          entries: d.entries,
        })),
      );

      // Set state
      setWeekDaysData(weekDays);
      setLoading(false);

      // Resolve with the weekDays so it can be used immediately
      resolve(weekDays);
    });
  };

  // Fetch WMR data for the selected week
  const fetchWMRForWeek = async (startDate, endDate) => {
    console.log("=== fetchWMRForWeek START ===");
    console.log("Dates:", startDate, endDate);

    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      // Format dates for API
      const weekStartStr = format(startDate, "yyyy-MM-dd");
      const weekEndStr = format(endDate, "yyyy-MM-dd");

      console.log("Fetching WMR for week:", weekStartStr, "to", weekEndStr);

      // Fetch WMR for the selected week and project
      const response = await axios.get(
        `${url}/api/wmr/fetch-by-project/${projectId}?weekStartDate=${weekStartStr}&weekEndDate=${weekEndStr}`,
        config,
      );

      console.log("API Response:", response.data);

      if (response.data.wmrs && response.data.wmrs.length > 0) {
        const wmr = response.data.wmrs[0];
        console.log("Found WMR:", wmr._id);

        // Validate week dates
        const wmrStartStr = format(parseISO(wmr.weekStartDate), "yyyy-MM-dd");
        const wmrEndStr = format(parseISO(wmr.weekEndDate), "yyyy-MM-dd");

        console.log("WMR dates:", wmrStartStr, "-", wmrEndStr);
        console.log("Requested dates:", weekStartStr, "-", weekEndStr);

        if (wmrStartStr === weekStartStr && wmrEndStr === weekEndStr) {
          console.log("✓ Week dates MATCH!");

          // Set the current WMR ID for updating
          setCurrentWMRId(wmr._id);

          // Store the WMR data in ref for later population
          pendingWMRRef.current = wmr;

          // Initialize week data FIRST
          console.log("Initializing week data...");
          await initializeWeekData(startDate, endDate);

          // Now populate with the stored WMR data
          console.log("Week data initialized, now populating WMR data");
          populateWMRData(wmr);

          showToast(
            "info",
            "WMR Found",
            "Existing WMR data loaded for this week.",
          );
          return true;
        } else {
          console.log("✗ Week dates DON'T match!");
          showToast(
            "warning",
            "Week Mismatch",
            "No WMR found for the selected week.",
          );
          return false;
        }
      } else {
        console.log("No WMRs found in response");
        return false;
      }
    } catch (err) {
      console.error("Error fetching WMR data:", err);
      showToast("error", "Error", "Failed to load WMR data.");
      return false;
    }
  };

  // Populate form data from existing WMR
  const populateWMRData = (wmr, weekDaysParam = null) => {
    console.log("=== populateWMRData Diagnostic (AddWMR) ===");
    console.log(
      "WMR ManpowerDeployment raw:",
      JSON.stringify(wmr.manpowerDeployment, null, 2),
    );
    console.log("weekDaysParam received:", weekDaysParam);
    console.log("Current weekDaysData state:", weekDaysData);

    // Use provided weekDays or fallback to state
    let weekDaysToUse = weekDaysParam || weekDaysData;

    // If weekDaysToUse is empty or not initialized, try to generate it
    if (!weekDaysToUse || weekDaysToUse.length === 0) {
      console.log("No weekDays available, trying to generate from WMR dates");
      if (wmr.weekStartDate && wmr.weekEndDate) {
        weekDaysToUse = generateWeekDaysData(
          new Date(wmr.weekStartDate),
          new Date(wmr.weekEndDate),
        );
        console.log("Generated weekDaysToUse:", weekDaysToUse);
      }
    }

    console.log(
      "Using weekDaysToUse:",
      weekDaysToUse.map((d) => ({
        day: d.day,
        date: d.date ? format(d.date, "yyyy-MM-dd") : "No date",
        entriesCount: (d.entries || []).length,
      })),
    );

    // ========== 1. Work Executed ==========
    if (wmr.workExtendedDuringWeek && wmr.workExtendedDuringWeek.length > 0) {
      console.log("Populating work executed data");
      const workExecuted = { ...wmrData.workExecuted };
      wmr.workExtendedDuringWeek.forEach((work) => {
        if (work.workType === "RCC Works")
          workExecuted.rccWork = work.description || "";
        else if (work.workType === "Civil Works")
          workExecuted.civilWorks = work.description || "";
        else if (work.workType === "Masonry / Finishing Works")
          workExecuted.masonryFinishing = work.description || "";
        else if (work.workType === "MEP Works")
          workExecuted.mepWorks = work.description || "";
        else if (work.workType === "External Development")
          workExecuted.externalDevelopment = work.description || "";
      });
      setWmrData((prev) => ({ ...prev, workExecuted }));
    }

    // ========== 2. Work In Progress ==========
    if (wmr.workInProgress && wmr.workInProgress.length > 0) {
      console.log("Populating work in progress data");
      const mergedProgress = [];

      // Backend sends workInProgress as a single array with all fields
      // Convert each entry to frontend format
      wmr.workInProgress.forEach((entry) => {
        mergedProgress.push({
          activity: entry.activity || "",
          location: entry.location || "",
          status: entry.status || "",
          remark: entry.remarks || "", // Note: backend uses 'remarks' field
        });
      });

      console.log("Processed workInProgress:", mergedProgress);

      setWmrData((prev) => ({
        ...prev,
        workInProgress: {
          mergedProgress: mergedProgress,
        },
      }));
    }

    // ========== 3. PLANNED VS ACTUAL PROGRESS (FIXED) ==========
    if (wmr.plannedActualProgress && wmr.plannedActualProgress.length > 0) {
      console.log("=== POPULATING PLANNED VS ACTUAL DATA ===");
      console.log(
        "Planned vs actual data from API:",
        wmr.plannedActualProgress,
      );

      // Create a DEEP COPY of weekDaysToUse
      const updatedWeekDays = JSON.parse(JSON.stringify(weekDaysToUse));
      console.log(
        "WeekDays before population:",
        updatedWeekDays.map((d) => ({
          day: d.day,
          date: format(d.date, "yyyy-MM-dd"),
          entries: d.entries,
        })),
      );

      // Clear any existing entries
      updatedWeekDays.forEach((day) => {
        day.entries = [];
      });

      // Process each entry from the WMR data
      wmr.plannedActualProgress.forEach((entry) => {
        if (!entry.dayDate) {
          console.log("Entry has no dayDate:", entry);
          return;
        }

        const entryDateStr =
          typeof entry.dayDate === "string"
            ? entry.dayDate.split("T")[0]
            : format(new Date(entry.dayDate), "yyyy-MM-dd");
        console.log(`Processing entry for date: ${entryDateStr}`);

        // Find matching day in weekDays
        const dayIndex = updatedWeekDays.findIndex((day) => {
          if (!day.date) return false;
          const dayDateStr = format(new Date(day.date), "yyyy-MM-dd");
          return dayDateStr === entryDateStr;
        });

        if (dayIndex !== -1) {
          console.log(
            `✓ MATCH FOUND! Adding entry to ${updatedWeekDays[dayIndex].day}`,
          );

          updatedWeekDays[dayIndex].entries.push({
            contractor: entry.contractorVendorName || "",
            activity: entry.targetedActivityDetails || "",
            status: entry.status || "OPEN",
            variance: entry.varianceReason || "",
            expectedCompletionDate: entry.expectedCompletionDate
              ? new Date(entry.expectedCompletionDate)
              : null,
          });

          console.log(
            `Day ${dayIndex} now has ${updatedWeekDays[dayIndex].entries.length} entries`,
          );
        } else {
          console.log(`✗ NO MATCH for date ${entryDateStr}`);
          console.log(
            "Available dates in weekDays:",
            updatedWeekDays.map((d) => format(d.date, "yyyy-MM-dd")),
          );
        }
      });

      console.log(
        "Final updatedWeekDays:",
        updatedWeekDays.map((d) => ({
          day: d.day,
          entriesCount: d.entries.length,
          entries: d.entries,
        })),
      );

      // IMPORTANT: Set the state with updated data
      setWeekDaysData(updatedWeekDays);
      console.log("setWeekDaysData called with updated data");
    } else {
      console.log("No planned actual progress data found in WMR");
    }

    // ========== 5. Manpower Deployment ==========
    if (wmr.manpowerDeployment && wmr.manpowerDeployment.length > 0) {
      console.log("Populating manpower deployment");

      // Explicitly generate the 6 dates of the week based on weekStartDate
      // Re-generate the 6 dates of the week for consistent mapping (dd/MM/yy)
      // Robust date generation to avoid timezone shifts
      const weekDates = [];
      let startDate;
      if (typeof wmr.weekStartDate === "string") {
        const [year, month, day] = wmr.weekStartDate
          .split("T")[0]
          .split("-")
          .map(Number);
        startDate = new Date(year, month - 1, day);
      } else {
        startDate = new Date(wmr.weekStartDate);
      }

      for (let i = 0; i < 6; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        weekDates.push(format(date, "dd/MM/yy"));
      }
      console.log("Generated weekDates for mapping in AddWMR:", weekDates);

      const laborGroups = {};
      wmr.manpowerDeployment.forEach((item) => {
        const laborType = (item.labourType || item.laborType || "").trim();
        if (!laborType) return;

        if (!laborGroups[laborType]) {
          laborGroups[laborType] = new Array(6).fill(0);
        }

        // Ensure dailyCount is a plain object (handle Mongoose Map)
        const dailyCount = item.dailyCount?.$__parent
          ? Object.fromEntries(item.dailyCount)
          : item.dailyCount?.toJSON
            ? item.dailyCount.toJSON()
            : item.dailyCount;

        if (dailyCount) {
          Object.entries(dailyCount).forEach(([date, count]) => {
            // Robust matching: normalize date string to dd/MM/yy
            const normalizeDate = (dStr) => {
              if (!dStr) return "";
              let s = dStr.toString().trim();

              // Handle ISO format YYYY-MM-DD...
              if (s.includes("-")) {
                const parts = s.split("T")[0].split("-");
                if (parts.length === 3) {
                  const [y, m, d] = parts;
                  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y.slice(-2)}`;
                }
              }

              // Handle DD/MM/YY or DD/MM/YYYY
              if (s.includes("/")) {
                const parts = s.split("/");
                if (parts.length === 3) {
                  const d = parts[0].padStart(2, "0");
                  const m = parts[1].padStart(2, "0");
                  let y = parts[2];
                  // Handle both 2-digit and 4-digit years
                  if (y.length === 4) {
                    y = y.slice(-2);
                  } else if (y.length === 2) {
                    y = y.padStart(2, "0");
                  } else {
                    y = y.padStart(2, "0");
                  }
                  return `${d}/${m}/${y}`;
                }
              }

              // Fallback for full date strings (e.g. from date.toString())
              try {
                const parsed = new Date(s);
                if (!isNaN(parsed.getTime())) {
                  return format(parsed, "dd/MM/yy");
                }
              } catch (e) {}

              return s;
            };

            const normalizedDate = normalizeDate(date);

            // Target search: Try direct or normalized match
            let dateIndex = weekDates.indexOf(normalizedDate);

            // Fallback: Digits-only matching
            if (dateIndex === -1) {
              const getDigits = (str) => str.replace(/\D/g, "").slice(-6);
              const searchDigits = getDigits(normalizedDate);
              dateIndex = weekDates.findIndex(
                (wd) => getDigits(wd) === searchDigits,
              );
            }

            if (dateIndex !== -1) {
              laborGroups[laborType][dateIndex] =
                (laborGroups[laborType][dateIndex] || 0) +
                (parseInt(count) || 0);
            } else {
              console.warn(
                `No match for date ${date} (normalized: ${normalizedDate}) in weekDates`,
                weekDates,
              );
            }
          });
        }
      });

      const laborTypes = Object.entries(laborGroups).map(([type, data]) => ({
        type,
        data: data.map((v) => v || 0), // Ensure no nulls
      }));

      setWmrData((prev) => ({
        ...prev,
        manpowerDeployment: {
          laborTypes,
          dates: weekDates,
        },
      }));
    } else {
      // Handle empty manpower deployment - show default structure
      console.log(
        "No manpower deployment data found, showing default structure",
      );
      const weekDates = [];
      const startDate = new Date(wmr.weekStartDate);
      for (let i = 0; i < 6; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        weekDates.push(format(date, "dd/MM/yy")); // Match database format
      }

      const defaultLaborTypes = [
        { type: "Engineer", data: [0, 0, 0, 0, 0, 0] },
        { type: "Supervisors", data: [0, 0, 0, 0, 0, 0] },
        { type: "Masons", data: [0, 0, 0, 0, 0, 0] },
        { type: "Helpers", data: [0, 0, 0, 0, 0, 0] },
      ];

      setWmrData((prev) => ({
        ...prev,
        manpowerDeployment: {
          laborTypes: defaultLaborTypes,
          dates: weekDates,
        },
      }));
    }

    // ========== 6. Machinery Equipment ==========
    if (wmr.machineryEquipment && wmr.machineryEquipment.length > 0) {
      console.log("Populating machinery equipment");
      setWmrData((prev) => ({
        ...prev,
        machineryEquipment: wmr.machineryEquipment.map((m) => ({
          equipment: m.typeOfEquipment || "",
          quantity: m.quantity?.toString() || "0",
          hours: m.workingHours?.toString() || "0",
          utilization: m.utilizationStatus || "",
        })),
      }));
    }

    // ========== 7. Material Status ==========
    if (wmr.materialStatus && wmr.materialStatus.length > 0) {
      console.log("Populating material status");
      setWmrData((prev) => ({
        ...prev,
        materialStatus: wmr.materialStatus.map((m) => ({
          description: m.materialDescription || "",
          received: m.materialReceived?.toString() || "0",
          consumed: m.materialConsumed?.toString() || "0",
          balance: (m.balanceAtSite || m.materialBalance)?.toString() || "0",
        })),
      }));
    }

    // ========== 8. Quality Control ==========
    if (
      wmr.qualityControlActivities &&
      wmr.qualityControlActivities.length > 0
    ) {
      console.log("Populating quality control");
      setWmrData((prev) => ({
        ...prev,
        qualityControl: wmr.qualityControlActivities.map((q) => ({
          inspection: q.inspectionCarriedOut || "",
          test: q.testConducted || "",
          observation: q.ncrObservation || "",
          status: q.status || "OPEN",
        })),
      }));
    }

    // ========== 9. Safety & HSE ==========
    if (wmr.safetyHSEStatus) {
      console.log("Populating safety HSE");
      setWmrData((prev) => ({
        ...prev,
        safetyHSE: {
          safetyCompliance: wmr.safetyHSEStatus.safetyCompliance || "",
          toolboxTalks: wmr.safetyHSEStatus.toolboxTalks || "",
          accidentsNearMisses: wmr.safetyHSEStatus.accidentsNearMisses || "",
        },
      }));
    }

    // ========== 10. Issues Constraints ==========
    if (wmr.issuesConstraints) {
      console.log("Populating issues constraints");
      setWmrData((prev) => ({
        ...prev,
        issuesConstraints: {
          technical: wmr.issuesConstraints.technical || "",
          material: wmr.issuesConstraints.material || "",
          manpower: wmr.issuesConstraints.manpower || "",
          designApproval: wmr.issuesConstraints.designApproval || "",
        },
      }));
    }

    // ========== 11. Instructions Decisions ==========
    if (wmr.instructionsDecisions && Array.isArray(wmr.instructionsDecisions)) {
      console.log("Populating instructions decisions");
      setWmrData((prev) => ({
        ...prev,
        instructionsDecisions: wmr.instructionsDecisions.map((instruction) => ({
          date: instruction.date ? new Date(instruction.date) : new Date(),
          visitorName: instruction.visitorName || "",
          designation: instruction.designation || "",
          instruction: instruction.instructions || "",
          remark: instruction.remarks || "",
        })),
      }));
    }

    // ========== 12. Decision Discussion ==========
    if (wmr.decisionRecords && wmr.decisionRecords.length > 0) {
      console.log("Populating decision discussion");
      setWmrData((prev) => ({
        ...prev,
        decisionDiscussion: wmr.decisionRecords[0].record || "",
      }));
    }

    // ========== 13. Plan for Next Week ==========
    if (wmr.planNextWeek && wmr.planNextWeek.length > 0) {
      console.log("Populating plan for next week");
      setWmrData((prev) => ({
        ...prev,
        planNextWeek: {
          targetActivities: wmr.planNextWeek[0].targetActivity || "",
          resourcePlanning: wmr.planNextWeek[0].resourcePlanning || "",
        },
      }));
    }

    // ========== 14. Report Files ==========
    if (wmr.reportFiles && wmr.reportFiles.length > 0) {
      console.log("Populating report files");
      const reportFiles = wmr.reportFiles.map((file) => ({
        name: file.fileName,
        size: 0,
        type: "application/pdf",
        path: file.filePath,
      }));
      setUploadedReports(reportFiles);
      setOriginalReportFiles(reportFiles); // Track original files for removal
    }

    // ========== 15. Photos ==========
    if (wmr.photographicProgress && wmr.photographicProgress.length > 0) {
      console.log("Populating photos");
      const photos = wmr.photographicProgress.map((photo) => ({
        photoPath: photo.photoPath,
        description: photo.description || "",
        shapes: photo.shapes || [],
        uploadedAt: photo.uploadedAt,
        name: photo.description || "Photo",
        imageUrl: `${url}${photo.photoPath.replace(/\\/g, "/")}`,
      }));
      setUploadedPhotos(photos);
      setOriginalPhotos(photos); // Track original photos for removal
    }

    // ========== 16. Project Layout Image ==========
    if (wmr.projectLayoutImage) {
      console.log(
        "AddWMR: Found project layout image:",
        wmr.projectLayoutImage,
      );
      // Convert backslashes to forward slashes and ensure proper URL format
      const imagePath = wmr.projectLayoutImage.filePath.replace(/\\/g, "/");
      const imageUrl = imagePath.startsWith("/")
        ? `${url}${imagePath}`
        : `${url}/${imagePath}`;
      setProjectLayoutImagePreview(imageUrl);
      console.log("AddWMR: Project layout image loaded:", imageUrl);
    } else {
      console.log("AddWMR: No project layout image found in WMR data");
    }

    console.log("=== populateWMRData COMPLETE ===");
  };

  // Handle week date selection (force Monday selection)
  const handleWeekDateSelect = async (date) => {
    if (!date) return;

    // Check if selected date is Monday
    if (!isMonday(date)) {
      showToast(
        "error",
        "Invalid Date",
        "Please select a Monday to start the week.",
      );
      return;
    }

    const monday = new Date(date);
    const saturday = addDays(monday, 5);

    setWeekStartDate(monday);
    setWeekEndDate(saturday);
    setShowDatePicker(false);

    // Reset current WMR ID when changing weeks
    setCurrentWMRId(null);

    // Reset WPR import status
    setIsWPRImported(false);
    setShowWPRImport(false);
    setWprData(null);

    // Clear all form data to prevent previous week data from showing
    setWmrData({
      workExecuted: {
        rccWork: "",
        civilWorks: "",
        masonryFinishing: "",
        mepWorks: "",
        externalDevelopment: "",
      },
      workInProgress: {
        mergedProgress: [],
      },
      plannedVsActual: {
        monday: { entries: [] },
        tuesday: { entries: [] },
        wednesday: { entries: [] },
        thursday: { entries: [] },
        friday: { entries: [] },
        saturday: { entries: [] },
      },
      manpowerDeployment: {
        laborTypes: [
          { type: "Engineer", data: [0, 0, 0, 0, 0, 0] },
          { type: "Supervisors", data: [0, 0, 0, 0, 0, 0] },
          { type: "Unskilled Labour", data: [0, 0, 0, 0, 0, 0] },
          { type: "Carpenter", data: [0, 0, 0, 0, 0, 0] },
        ],
        dates: [],
      },
      machineryEquipment: [
        { equipment: "Excavator", quantity: "0", hours: "0", utilization: "" },
        { equipment: "JCB", quantity: "0", hours: "0", utilization: "" },
        {
          equipment: "Concrete Mixer",
          quantity: "0",
          hours: "0",
          utilization: "",
        },
        {
          equipment: "Tower Crane",
          quantity: "0",
          hours: "0",
          utilization: "",
        },
        {
          equipment: "Mobile Crane",
          quantity: "0",
          hours: "0",
          utilization: "",
        },
        {
          equipment: "Concrete Pumping",
          quantity: "0",
          hours: "0",
          utilization: "",
        },
        { equipment: "Vibrator", quantity: "0", hours: "0", utilization: "" },
        { equipment: "Compactor", quantity: "0", hours: "0", utilization: "" },
        {
          equipment: "Bar Bending Machine",
          quantity: "0",
          hours: "0",
          utilization: "",
        },
        {
          equipment: "Bar Cutting Machine",
          quantity: "0",
          hours: "0",
          utilization: "",
        },
        {
          equipment: "Welding Machine",
          quantity: "0",
          hours: "0",
          utilization: "",
        },
        {
          equipment: "Generator / DG Set",
          quantity: "0",
          hours: "0",
          utilization: "",
        },
      ],
      materialStatus: [
        {
          description: "Cement (OPC / PPC)",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        {
          description: "Steel / TMT Bars",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        {
          description: "Coarse Aggregate (20mm)",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        {
          description: "Coarse Aggregate (10mm)",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        {
          description: "Fine Aggregate / Sand",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        { description: "Bricks", received: "0", consumed: "0", balance: "0" },
        {
          description: "Ready Mix Concrete",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        {
          description: "Concrete Blocks",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        {
          description: "Plywood / Shuttering",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        {
          description: "Binding Wire",
          received: "0",
          consumed: "0",
          balance: "0",
        },
        { description: "Nails", received: "0", consumed: "0", balance: "0" },
        {
          description: "MS Channels / Angles",
          received: "0",
          consumed: "0",
          balance: "0",
        },
      ],
      qualityControl: [
        {
          inspection: "What Inspection we Carried out",
          test: "0",
          observation: "",
        },
        { inspection: "Inspection 1", test: "0", observation: "" },
        { inspection: "Inspection seconds", test: "0", observation: "" },
      ],
      safetyHSE: {
        safetyCompliance: "",
        toolboxTalks: "",
        accidentsNearMisses: "",
      },
      issuesConstraints: {
        technical: "",
        material: "",
        manpower: "",
        designApproval: "",
      },
      instructionsDecisions: [],
      reports: [],
      decisionDiscussion: "",
      planNextWeek: {
        targetActivities: "",
        resourcePlanning: "",
      },
      photos: [],
    });

    // Clear uploaded files
    setUploadedReports([]);
    setUploadedPhotos([]);
    setOriginalReportFiles([]); // Clear original files tracking

    // Clear project layout image
    setProjectLayoutImage(null);
    setProjectLayoutImagePreview(null);

    // Initialize week data - useEffect will handle WMR fetching
    initializeWeekData(monday, saturday);

    showToast(
      "success",
      "Week Selected",
      `Week: ${formatDateForWeekHeader(monday)} - ${formatDateForWeekHeader(saturday)}`,
    );
  };

  // Fetch WPR data for import
  const fetchWPRForImport = async () => {
    if (!weekStartDate || !weekEndDate) {
      showToast("error", "Error", "Please select a week first.");
      return;
    }

    try {
      setIsImporting(true);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      // Format dates for API
      const weekStartStr = format(weekStartDate, "yyyy-MM-dd");
      const weekEndStr = format(weekEndDate, "yyyy-MM-dd");

      // Fetch WPRs for the selected week
      const wprResponse = await axios.get(
        `${url}/api/wpr/fetch-by-project/${projectId}`,
        config,
      );

      if (wprResponse.data.wprs && wprResponse.data.wprs.length > 0) {
        // Find WPR for the exact week
        const wprForWeek = wprResponse.data.wprs.find((wpr) => {
          if (!wpr.weekStartDate || !wpr.weekEndDate) return false;

          const wprStart = format(parseISO(wpr.weekStartDate), "yyyy-MM-dd");
          const wprEnd = format(parseISO(wpr.weekEndDate), "yyyy-MM-dd");

          return wprStart === weekStartStr && wprEnd === weekEndStr;
        });

        if (wprForWeek) {
          setWprData(wprForWeek);
          // Save original data before import
          setOriginalWeekDaysData([...weekDaysData]);
          processWPRData(wprForWeek);
          setIsWPRImported(true);
          showToast(
            "success",
            "WPR Found",
            "Weekly Planning Report data loaded successfully!",
          );
        } else {
          showToast(
            "warning",
            "No WPR Found",
            "No WPR found for the selected week.",
          );
        }
      } else {
        showToast("warning", "No WPR Found", "No WPR found for this project.");
      }
    } catch (err) {
      console.error("Error fetching WPR:", err);
      showToast("error", "Error", "Failed to fetch WPR data.");
    } finally {
      setIsImporting(false);
    }
  };

  // Process WPR data for import into WMR
  const processWPRData = (wpr) => {
    if (!wpr.dailyEntries || !Array.isArray(wpr.dailyEntries)) return;

    // Process planned vs actual section
    const updatedWeekDays = [...weekDaysData];

    wpr.dailyEntries.forEach((dayEntry) => {
      const dayIndex = updatedWeekDays.findIndex(
        (day) =>
          day.day === dayEntry.day &&
          format(new Date(day.date), "yyyy-MM-dd") ===
            format(parseISO(dayEntry.date), "yyyy-MM-dd"),
      );

      if (dayIndex !== -1) {
        updatedWeekDays[dayIndex].entries = dayEntry.entries.map((entry) => ({
          contractor: entry.contractorVendor || "",
          activity: entry.details || "",
          status: entry.status || "OPEN",
          variance: entry.remarks || "",
          expectedCompletionDate: null,
        }));
      }
    });

    // Reset current WMR ID when changing weeks
    setCurrentWMRId(null);
  };

  // Handle planned vs actual entry
  const handleAddPlannedVsActualEntry = (dayIndex) => {
    // Validation: If status is OPEN, variance and expected completion date are required
    if (newEntry.status === "OPEN") {
      if (!newEntry.variance || newEntry.variance.trim() === "") {
        showToast(
          "error",
          "Validation Error",
          "Variance/Reason is required when status is Open",
        );
        return;
      }
      if (!newEntry.expectedCompletionDate) {
        showToast(
          "error",
          "Validation Error",
          "Expected Completion Date is required when status is Open",
        );
        return;
      }
    }

    const updatedWeekDays = [...weekDaysData];
    if (!updatedWeekDays[dayIndex].entries) {
      updatedWeekDays[dayIndex].entries = [];
    }

    if (
      editingEntry.section === "plannedActual" &&
      editingEntry.entryIndex !== null
    ) {
      // Update existing entry
      updatedWeekDays[dayIndex].entries[editingEntry.entryIndex] = {
        ...newEntry,
      };
      setEditingEntry({
        section: null,
        dayIndex: null,
        entryIndex: null,
        data: null,
      });
    } else {
      // Add new entry
      updatedWeekDays[dayIndex].entries.push({ ...newEntry });
    }

    setWeekDaysData(updatedWeekDays);
    if (clearNewEntryPersistence) clearNewEntryPersistence();
    setNewEntry({
      contractor: "",
      activity: "",
      status: "OPEN",
      variance: "",
      expectedCompletionDate: null,
    });
    setOpenDay(null);
    setShowWPRImport(true);
  };

  // Remove WPR import data
  const removeWPRImport = () => {
    // Reset to completely empty state with just dates
    const resetWeekDays =
      weekDaysData &&
      weekDaysData.map((day) => ({
        ...day,
        entries: [], // Empty array instead of any imported data
      }));
    setWeekDaysData(resetWeekDays || []);

    setIsWPRImported(false);
    setShowWPRImport(false);
    setWprData(null);
    showToast(
      "info",
      "WPR Import Removed",
      "WPR data has been removed. You can now add entries manually.",
    );
  };

  // Handle planned vs actual entry deletion
  const handleDeletePlannedVsActualEntry = (dayIndex, entryIndex) => {
    const updatedWeekDays = [...weekDaysData];
    updatedWeekDays[dayIndex].entries.splice(entryIndex, 1);
    setWeekDaysData(updatedWeekDays);
  };

  // Handle numeric input validation
  const handleNumericInput = (e, setterFunction) => {
    let value = e.target.value;
    // Allow only numbers and empty string
    if (value === "" || /^\d*$/.test(value)) {
      // Remove leading zero if length > 1 (e.g., "02" becomes "2")
      if (value.length > 1 && value.startsWith("0")) {
        value = value.replace(/^0+/, "");
      }
      setterFunction(value);
    }
  };

  // Handle cut operation for 0 values
  const handleCutOperation = (e, setterFunction) => {
    setTimeout(() => {
      if (e.target.value === "0") {
        setterFunction("");
      }
    }, 0);
  };

  // Handle manpower deployment change
  const handleManpowerChange = (typeIndex, dayIndex, value) => {
    const updatedLaborTypes = [...wmrData.manpowerDeployment.laborTypes];
    // Keep as string to allow empty value (clearing 0)
    updatedLaborTypes[typeIndex].data[dayIndex] = value;

    setWmrData((prev) => ({
      ...prev,
      manpowerDeployment: {
        ...prev.manpowerDeployment,
        laborTypes: updatedLaborTypes,
      },
    }));
  };

  // Handle labor type name change
  const handleLaborTypeChange = (typeIndex, newType) => {
    const updatedLaborTypes = [...wmrData.manpowerDeployment.laborTypes];
    const oldType = updatedLaborTypes[typeIndex].type;
    updatedLaborTypes[typeIndex].type = newType;

    // Update manpower values calculations
    const updatedManpowerValues = calculateManpowerValues();
    delete updatedManpowerValues.averages[oldType];
    delete updatedManpowerValues.totals[oldType];

    setWmrData((prev) => ({
      ...prev,
      manpowerDeployment: {
        ...prev.manpowerDeployment,
        laborTypes: updatedLaborTypes,
      },
    }));
  };

  // Handle labor type delete
  const handleDeleteLaborType = (typeIndex) => {
    const updatedLaborTypes = wmrData.manpowerDeployment.laborTypes.filter(
      (_, index) => index !== typeIndex,
    );
    setWmrData((prev) => ({
      ...prev,
      manpowerDeployment: {
        ...prev.manpowerDeployment,
        laborTypes: updatedLaborTypes,
      },
    }));
    showToast(
      "success",
      "Labor Type Deleted",
      "Labor type has been removed successfully.",
    );
  };

  // Add new labor type
  const handleAddLaborType = () => {
    setWmrData((prev) => ({
      ...prev,
      manpowerDeployment: {
        ...prev.manpowerDeployment,
        laborTypes: [
          ...prev.manpowerDeployment.laborTypes,
          {
            type: `New Labor ${prev.manpowerDeployment.laborTypes.length + 1}`,
            data: [0, 0, 0, 0, 0, 0],
          },
        ],
      },
    }));
  };

  // Handle machinery equipment change
  const handleMachineChange = (index, field, value) => {
    const updatedMachinery = [...wmrData.machineryEquipment];
    updatedMachinery[index][field] = value;
    setWmrData((prev) => ({
      ...prev,
      machineryEquipment: updatedMachinery,
    }));
  };

  // Handle machinery equipment deletion
  const handleDeleteMachine = (index) => {
    const updatedMachinery = [...wmrData.machineryEquipment];
    updatedMachinery.splice(index, 1);
    setWmrData((prev) => ({
      ...prev,
      machineryEquipment: updatedMachinery,
    }));
  };

  // Handle material status change
  const handleMaterialChange = (index, field, value) => {
    const updatedMaterials = [...wmrData.materialStatus];
    updatedMaterials[index][field] = value;

    // Calculate balance if received or consumed changed
    if (field === "received" || field === "consumed") {
      const received = parseFloat(updatedMaterials[index].received) || 0;
      const consumed = parseFloat(updatedMaterials[index].consumed) || 0;
      const balance = received - consumed;
      updatedMaterials[index].balance = balance.toString();

      if (balance < 0) {
        showToast(
          "warning",
          "Negative Balance",
          `Consumed quantity exceeds received quantity for ${updatedMaterials[index].description || "this item"}.`,
        );
      }
    }

    setWmrData((prev) => ({
      ...prev,
      materialStatus: updatedMaterials,
    }));
  };

  // Handle material deletion
  const handleDeleteMaterial = (index) => {
    const updatedMaterials = [...wmrData.materialStatus];
    updatedMaterials.splice(index, 1);
    setWmrData((prev) => ({
      ...prev,
      materialStatus: updatedMaterials,
    }));
  };

  // Handle visitor change
  const handleVisitorChange = (index, field, value) => {
    const updatedVisitors = [...wmrData.instructionsDecisions];
    updatedVisitors[index][field] = value;
    setWmrData((prev) => ({
      ...prev,
      instructionsDecisions: updatedVisitors,
    }));
  };

  // Handle visitor deletion
  const handleDeleteVisitor = (index) => {
    const updatedVisitors = [...wmrData.instructionsDecisions];
    updatedVisitors.splice(index, 1);
    setWmrData((prev) => ({
      ...prev,
      instructionsDecisions: updatedVisitors,
    }));
  };

  // Handle edit visitor
  const handleEditVisitor = (index) => {
    const visitor = wmrData.instructionsDecisions[index];
    setEditingVisitor(index);
    setNewVisitor({
      date: visitor.date,
      visitorName: visitor.visitorName,
      designation: visitor.designation,
      otherDesignation:
        visitor.designation === "Other Consultant" ? visitor.designation : "",
      instruction: visitor.instruction,
      remark: visitor.remark,
    });
    setShowOtherConsultantInput(visitor.designation === "Other Consultant");
    setShowVisitorEntry(true);
  };

  // Handle designation change
  const handleDesignationChange = (value) => {
    setNewVisitor({ ...newVisitor, designation: value });
    setShowOtherConsultantInput(value === "Other Consultant");
    if (value !== "Other Consultant") {
      setNewVisitor((prev) => ({ ...prev, otherDesignation: "" }));
    }
  };

  // Handle add visitor
  const handleAddVisitor = () => {
    const finalDesignation =
      newVisitor.designation === "Other Consultant"
        ? newVisitor.otherDesignation
        : newVisitor.designation;

    const visitorData = {
      ...newVisitor,
      designation: finalDesignation,
    };

    if (editingVisitor !== null) {
      // Update existing visitor
      const updatedVisitors = [...wmrData.instructionsDecisions];
      updatedVisitors[editingVisitor] = visitorData;
      setWmrData((prev) => ({
        ...prev,
        instructionsDecisions: updatedVisitors,
      }));
    } else {
      // Add new visitor
      setWmrData((prev) => ({
        ...prev,
        instructionsDecisions: [...prev.instructionsDecisions, visitorData],
      }));
    }

    // Reset form
    setNewVisitor({
      date: new Date(),
      visitorName: "",
      designation: "",
      otherDesignation: "",
      instruction: "",
      remark: "",
    });
    setShowVisitorEntry(false);
    setEditingVisitor(null);
    setShowOtherConsultantInput(false);
  };

  // Handle quality change
  const handleQualityChange = (index, field, value) => {
    const updatedQuality = [...wmrData.qualityControl];
    updatedQuality[index][field] = value;
    setWmrData((prev) => ({
      ...prev,
      qualityControl: updatedQuality,
    }));
  };

  // Handle quality control deletion
  const handleDeleteQuality = (index) => {
    const updatedQuality = [...wmrData.qualityControl];
    updatedQuality.splice(index, 1);
    setWmrData((prev) => ({
      ...prev,
      qualityControl: updatedQuality,
    }));
  };

  // Handle add machine
  const handleAddMachine = () => {
    setWmrData((prev) => ({
      ...prev,
      machineryEquipment: [...prev.machineryEquipment, { ...newMachine }],
    }));
    setNewMachine({ equipment: "", quantity: "", hours: "", utilization: "-" });
    setShowMachineEntry(false);
  };

  // Handle add material
  const handleAddMaterial = () => {
    // Calculate balance before adding
    const received = parseFloat(newMaterial.received) || 0;
    const consumed = parseFloat(newMaterial.consumed) || 0;
    const balanceVal = received - consumed;
    const balance = balanceVal.toString();

    if (balanceVal < 0) {
      showToast(
        "warning",
        "Negative Balance",
        "Consumed quantity exceeds received quantity. Please verify the entries.",
      );
    }

    setWmrData((prev) => ({
      ...prev,
      materialStatus: [...prev.materialStatus, { ...newMaterial, balance }],
    }));
    setNewMaterial({
      description: "",
      received: "",
      consumed: "",
      balance: "",
    });
    setShowMaterialEntry(false);
  };

  // Handle add quality
  const handleAddQuality = () => {
    setWmrData((prev) => ({
      ...prev,
      qualityControl: [...prev.qualityControl, { ...newQuality }],
    }));
    setNewQuality({
      inspection: "",
      test: "",
      observation: "",
      status: "OPEN",
    });
    setShowQualityEntry(false);
  };

  // Handle merged work in progress entry
  const handleAddProgressEntry = () => {
    if (editingEntry.section === "progress" && editingEntry.index !== null) {
      // Update existing entry
      const updatedProgress = [...wmrData.workInProgress.mergedProgress];
      updatedProgress[editingEntry.index] = { ...newProgressEntry };
      setWmrData((prev) => ({
        ...prev,
        workInProgress: { mergedProgress: updatedProgress },
      }));
      setEditingEntry({ section: null, index: null, data: null });
    } else {
      // Add new entry with defensive check for mergedProgress
      setWmrData((prev) => ({
        ...prev,
        workInProgress: {
          mergedProgress: [
            ...(prev.workInProgress?.mergedProgress || []),
            { ...newProgressEntry },
          ],
        },
      }));
    }
    setNewProgressEntry({ activity: "", location: "", status: "", remark: "" });
    setShowActivityForm(false);
  };

  const handleDeleteProgressEntry = (index) => {
    const updatedProgress = [...wmrData.workInProgress.mergedProgress];
    updatedProgress.splice(index, 1);
    setWmrData((prev) => ({
      ...prev,
      workInProgress: {
        mergedProgress: updatedProgress,
      },
    }));
  };

  // Handle report upload
  const handleReportUpload = (event) => {
    const files = Array.from(event.target.files);
    setUploadedReports((prev) => [...prev, ...files]);
  };

  const handleRemoveReport = (index) => {
    setUploadedReports((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle photo upload with shape drawing
  const handlePhotoUploadWithDrawing = (photoData) => {
    console.log("AddWMR: Photo upload data received:", {
      hasFile: !!photoData.file,
      title: photoData.title,
      titleLength: photoData.title?.length,
      shapesCount: photoData.shapes?.length || 0,
    });

    // Ensure title is never empty
    const finalTitle =
      photoData.title?.trim() ||
      photoData.file?.name?.split(".")[0] ||
      "Untitled Photo";

    const newPhoto = {
      file: photoData.file,
      title: finalTitle,
      shapes: photoData.shapes,
      imageUrl: photoData.imageUrl,
      name: photoData.file?.name || photoData.name,
      size: photoData.file?.size || photoData.size,
      type: photoData.file?.type || photoData.type,
    };

    console.log("AddWMR: Final photo object created:", {
      title: newPhoto.title,
      fileName: newPhoto.name,
    });

    const updatedPhotos = [...uploadedPhotos, newPhoto];
    setUploadedPhotos(updatedPhotos);
    
    // Update persistence
    setWmrData({
      ...wmrData,
      photos: updatedPhotos.map(p => ({
        title: p.title,
        shapes: p.shapes,
        imageUrl: p.imageUrl,
        name: p.name,
        size: p.size,
        type: p.type
      }))
    });

    setShowPhotoUploadDialog(false);
    showToast(
      "success",
      "Photo Added",
      "Photo with annotations added successfully!",
    );
  };

  const removePhoto = (index) => {
    const updatedPhotos = uploadedPhotos.filter((_, i) => i !== index);
    setUploadedPhotos(updatedPhotos);
    setWmrData({
      ...wmrData,
      photos: updatedPhotos.map(p => ({
        title: p.title,
        shapes: p.shapes,
        imageUrl: p.imageUrl,
        name: p.name,
        size: p.size,
        type: p.type
      }))
    });
  };

  // Handle project layout image upload
  const handleProjectLayoutImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (image only)
    if (!file.type.startsWith("image/")) {
      showToast(
        "error",
        "Error",
        "Only image files are allowed for project layout.",
      );
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast("error", "Error", "File size must be less than 10MB.");
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = () => {
      const base64 = fileReader.result;
      setProjectLayoutImagePreview(base64);
      setWmrData((prev) => ({
        ...prev,
        projectLayoutImagePreview: base64,
        projectLayoutImageName: file.name
      }));
    };
    fileReader.readAsDataURL(file);
    setProjectLayoutImage(file);
    showToast(
      "success",
      "Success",
      "Project layout image uploaded successfully!",
    );
  };

  // Remove project layout image
  const removeProjectLayoutImage = () => {
    setProjectLayoutImage(null);
    setProjectLayoutImagePreview(null);
    setWmrData((prev) => ({
      ...prev,
      projectLayoutImagePreview: null,
      projectLayoutImageName: ""
    }));
    showToast("info", "Removed", "Project layout image removed.");
  };

  // Download project layout image
  const downloadProjectLayoutImage = () => {
    if (!projectLayoutImage || !projectLayoutImagePreview) {
      showToast(
        "error",
        "Error",
        "No project layout image available for download.",
      );
      return;
    }

    try {
      // Create download link
      const link = document.createElement("a");
      link.href = projectLayoutImagePreview;
      link.download = projectLayoutImage.name || "project-layout.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast(
        "success",
        "Success",
        "Project layout image downloaded successfully!",
      );
    } catch (error) {
      console.error("Error downloading project layout image:", error);
      showToast("error", "Error", "Failed to download project layout image.");
    }
  };

  // Calculate manpower totals and averages
  const calculateManpowerValues = () => {
    if (!wmrData.manpowerDeployment?.laborTypes) {
      return {
        totals: {},
        averages: {},
        columnTotals: [0, 0, 0, 0, 0, 0],
        grandTotal: 0,
        grandAverage: 0,
      };
    }

    const laborTypes = wmrData.manpowerDeployment.laborTypes;
    const totals = {};
    const averages = {};
    const columnTotals = [0, 0, 0, 0, 0, 0];
    let grandTotal = 0;

    laborTypes.forEach((labor) => {
      const values = labor.data;
      const total = values.reduce(
        (sum, val) => sum + (parseFloat(val) || 0),
        0,
      );
      const average = values.length > 0 ? Math.round(total / values.length) : 0;

      totals[labor.type] = total;
      averages[labor.type] = average;
      grandTotal += total;

      values.forEach((val, index) => {
        columnTotals[index] += parseFloat(val) || 0;
      });
    });

    const columnAverages = columnTotals.map((total) =>
      laborTypes.length > 0 ? Math.round(total / laborTypes.length) : 0,
    );

    const grandAverage =
      laborTypes.length > 0
        ? Math.round(grandTotal / (laborTypes.length * 6))
        : 0;

    return {
      totals,
      averages,
      columnTotals,
      columnAverages,
      grandTotal,
      grandAverage,
    };
  };

  // Save WMR
  const handleSaveWMR = async () => {
    try {
      setSaving(true);

      const formData = new FormData();

      // Add basic fields
      formData.append("projectId", projectId);
      formData.append("weekStartDate", format(weekStartDate, "yyyy-MM-dd"));
      formData.append("weekEndDate", format(weekEndDate, "yyyy-MM-dd"));
      formData.append(
        "title",
        `WMR - ${formatDateForWeekHeader(weekStartDate)} to ${formatDateForWeekHeader(weekEndDate)}`,
      );

      // Add work executed sections with rich text formatting - match backend schema
      formData.append(
        "workExtendedDuringWeek",
        JSON.stringify([
          { workType: "RCC Works", description: wmrData.workExecuted.rccWork },
          {
            workType: "Civil Works",
            description: wmrData.workExecuted.civilWorks,
          },
          {
            workType: "Masonry / Finishing Works",
            description: wmrData.workExecuted.masonryFinishing,
          },
          { workType: "MEP Works", description: wmrData.workExecuted.mepWorks },
          {
            workType: "External Development",
            description: wmrData.workExecuted.externalDevelopment,
          },
        ]),
      );

      // Add work in progress (merged)
      const workInProgressData = wmrData.workInProgress.mergedProgress.map(
        (entry) => ({
          activity: entry.activity || "",
          status: entry.status || "",
          location: entry.location || "",
          remarks: entry.remark || "",
        }),
      );
      formData.append("workInProgress", JSON.stringify(workInProgressData));

      // Add location progress (extracted from merged data)
      const locationProgressData = wmrData.workInProgress.mergedProgress
        .filter((entry) => entry.location || entry.remark)
        .map((entry) => ({
          locationArea: entry.location || "",
          progress: entry.remark || "",
        }));
      formData.append("locationProgress", JSON.stringify(locationProgressData));

      // Add planned vs actual data - match backend schema
      const plannedVsActualData = [];
      weekDaysData.forEach((day) => {
        day.entries.forEach((entry) => {
          plannedVsActualData.push({
            dayDate: format(day.date, "yyyy-MM-dd"),
            contractorVendorName: entry.contractor || "",
            targetedActivityDetails: entry.activity || "",
            status: entry.status || "OPEN",
            varianceReason: entry.variance || "",
            expectedCompletionDate: entry.expectedCompletionDate
              ? format(entry.expectedCompletionDate, "yyyy-MM-dd")
              : null,
          });
        });
      });
      formData.append(
        "plannedActualProgress",
        JSON.stringify(plannedVsActualData),
      );

      // Add manpower deployment - match backend schema (Group by labourType)
      const manpowerDataGroups = [];
      wmrData.manpowerDeployment.laborTypes.forEach((labor) => {
        const dailyCount = {};
        labor.data.forEach((count, index) => {
          const date = wmrData.manpowerDeployment.dates[index];
          if (date) {
            // Ensure count is a number, default to 0 if invalid
            const numCount = parseInt(count) || 0;
            dailyCount[date] = numCount;
          }
        });

        manpowerDataGroups.push({
          labourType: labor.type,
          dailyCount: dailyCount,
        });
      });
      formData.append("manpowerDeployment", JSON.stringify(manpowerDataGroups));

      // Add machinery equipment - match backend schema
      const machineryData = wmrData.machineryEquipment.map((machine) => ({
        typeOfEquipment: machine.equipment || "",
        quantity: parseInt(machine.quantity) || 0,
        workingHours: parseInt(machine.hours) || 0,
        utilizationStatus: machine.utilization || "",
      }));
      formData.append("machineryEquipment", JSON.stringify(machineryData));

      // Add material status - match backend schema
      const materialData = wmrData.materialStatus.map((material) => ({
        materialDescription: material.description || "",
        materialReceived: parseFloat(material.received) || 0,
        materialConsumed: parseFloat(material.consumed) || 0,
        balanceAtSite: parseFloat(material.balance) || 0,
      }));
      formData.append("materialStatus", JSON.stringify(materialData));

      // Add quality control activities - match backend schema
      const qualityData = wmrData.qualityControl.map((quality) => ({
        inspectionCarriedOut: quality.inspection || "",
        testConducted: quality.test || "",
        ncrObservation: quality.observation || "",
        status: quality.status || "OPEN",
      }));
      formData.append("qualityControlActivities", JSON.stringify(qualityData));

      // Add safety & HSE status - match backend schema
      formData.append("safetyHSEStatus", JSON.stringify(wmrData.safetyHSE));

      // Add issues/constraints - match backend schema
      formData.append(
        "issuesConstraints",
        JSON.stringify(wmrData.issuesConstraints),
      );

      // Add instructions/decisions - match backend schema
      const visitorData = wmrData.instructionsDecisions.map((entry) => ({
        date: format(entry.date, "yyyy-MM-dd"),
        visitorName: entry.visitorName || "",
        designation: entry.designation || "",
        instructions: entry.instruction || "",
        remarks: entry.remark || "",
      }));
      formData.append("instructionsDecisions", JSON.stringify(visitorData));

      // Add decision discussion - match backend schema
      const decisionData = [
        {
          record: wmrData.decisionDiscussion || "",
          date: new Date().toISOString(),
        },
      ];
      formData.append("decisionRecords", JSON.stringify(decisionData));

      // Add plan for next week - match backend schema
      const planData = [
        {
          targetActivity: wmrData.planNextWeek.targetActivities || "",
          resourcePlanning: wmrData.planNextWeek.resourcePlanning || "",
        },
      ];
      formData.append("planNextWeek", JSON.stringify(planData));

      // Add report files - only append actual File objects, not database files
      console.log(
        "Frontend: Adding report files to FormData:",
        uploadedReports.length,
      );
      console.log("Frontend: uploadedReports array:", uploadedReports);
      uploadedReports.forEach((file, index) => {
        console.log(`Frontend: Adding report file ${index}:`, {
          name: file.name,
          type: file.type,
          size: file.size,
          isFile: file instanceof File,
        });

        // Only append actual File objects, not database-loaded files
        if (file instanceof File && file.size > 0) {
          formData.append(`reportFiles`, file);
          console.log(`Frontend: Appended actual file: ${file.name}`);
        } else {
          console.log(
            `Frontend: Skipped database file: ${file.name} (not a File object)`,
          );
        }
      });

      // Log all FormData entries to verify
      console.log("Frontend: FormData entries before sending:");
      for (let [key, value] of formData.entries()) {
        if (key === "reportFiles") {
          console.log(`  ${key}:`, value.name, value.type, value.size);
        } else if (key.startsWith("photo_")) {
          console.log(`  ${key}:`, value);
        } else {
          console.log(
            `  ${key}:`,
            typeof value === "string" ? value.substring(0, 100) + "..." : value,
          );
        }
      }

      console.log("Frontend: originalReportFiles:", originalReportFiles);
      console.log("Frontend: uploadedReports:", uploadedReports);

      const removedReportFiles = originalReportFiles.filter(
        (originalFile) =>
          !uploadedReports.some(
            (currentFile) =>
              currentFile.path === originalFile.path ||
              (currentFile instanceof File &&
                currentFile.name === originalFile.name),
          ),
      );

      if (removedReportFiles.length > 0) {
        console.log("Frontend: Adding removed report files information");
        formData.append(
          "removedReportFiles",
          JSON.stringify(removedReportFiles),
        );
      }

      // Handle removed photos
      console.log("Frontend: originalPhotos:", originalPhotos);
      console.log("Frontend: uploadedPhotos:", uploadedPhotos);

      const removedPhotos = originalPhotos.filter(
        (originalPhoto) =>
          !uploadedPhotos.some(
            (currentPhoto) =>
              (currentPhoto.photoPath &&
                currentPhoto.photoPath === originalPhoto.photoPath) ||
              (currentPhoto.file && currentPhoto.name === originalPhoto.name),
          ),
      );

      console.log("Frontend: removedPhotos:", removedPhotos);

      if (removedPhotos.length > 0) {
        console.log("Frontend: Adding removed photos information");
        formData.append("removedPhotographs", JSON.stringify(removedPhotos));
      }

      // Add photographs with shapes data
      console.log(
        "Frontend: Adding photographs to FormData:",
        uploadedPhotos.length,
      );
      let fileIndex = 0; // Use sequential index for files being uploaded
      uploadedPhotos.forEach((photo, index) => {
        if (photo.file) {
          console.log(`Frontend: Adding photograph ${fileIndex}:`, {
            title: photo.title,
            titleLength: photo.title?.length,
            fileName: photo.file.name,
            fileSize: photo.file.size,
            shapesCount: photo.shapes?.length || 0,
          });
          formData.append(`photographs`, photo.file);
          // Add shapes data as separate field using fileIndex
          formData.append(
            `photo_${fileIndex}_shapes`,
            JSON.stringify(photo.shapes || []),
          );
          formData.append(`photo_${fileIndex}_title`, photo.title || "");
          console.log(
            `Frontend: Appended photo_${fileIndex}_title:`,
            photo.title || "",
          );
          fileIndex++; // Increment only for files being uploaded
        } else {
          console.log(`Frontend: Skipping photograph ${index} - no file`);
        }
      });

      // Add project layout image if present
      if (projectLayoutImage) {
        formData.append("projectLayoutImage", projectLayoutImage);
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "multipart/form-data",
        },
      };

      // Save to backend using the correct endpoint
      let response;
      if (currentWMRId) {
        // Update existing WMR
        console.log(
          "Frontend: Sending UPDATE request to:",
          `${url}/api/wmr/update/${currentWMRId}`,
        );
        console.log("Frontend: FormData entries being sent:");
        for (let [key, value] of formData.entries()) {
          if (key === "reportFiles") {
            console.log(`  ${key}:`, value.name, value.type, value.size);
          } else {
            console.log(
              `  ${key}:`,
              typeof value === "string"
                ? value.substring(0, 100) + "..."
                : value,
            );
          }
        }

        response = await axios.put(
          `${url}/api/wmr/update/${currentWMRId}`,
          formData,
          config,
        );
      } else {
        // Create new WMR
        console.log(
          "Frontend: Sending CREATE request to:",
          `${url}/api/wmr/add`,
        );
        console.log("Frontend: FormData entries being sent:");
        for (let [key, value] of formData.entries()) {
          if (key === "reportFiles") {
            console.log(`  ${key}:`, value.name, value.type, value.size);
          } else {
            console.log(
              `  ${key}:`,
              typeof value === "string"
                ? value.substring(0, 100) + "..."
                : value,
            );
          }
        }

        response = await axios.post(`${url}/api/wmr/add`, formData, config);
      }

      if (response.data.message) {
        clearPersistence(); // Clear saved form data on success
        localStorage.removeItem(`wmr-photo-draft-${persistenceKey}`); // Clear photo annotation draft
        const action = currentWMRId ? "updated" : "created";
        showToast(
          "success",
          "Success",
          `Weekly Monitoring Report ${action} successfully!`,
        );
        setTimeout(() => navigate(-1), 1500);
      } else {
        showToast(
          "error",
          "Error",
          response.data.message || "Failed to save WMR.",
        );
      }
    } catch (err) {
      console.error("Error saving WMR:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save report.",
      );
    } finally {
      setSaving(false);
    }
  };

  // Show toast function
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={2}>
        <Alert severity="warning">Project not found</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const manpowerValues = calculateManpowerValues();
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const dayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={2} sx={{ fontFamily: FONT_FAMILY }}>
        {/* ================= HEADER ================= */}
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H2,
                color: COLORS.PRIMARY,
                fontFamily: FONT_FAMILY,
              }}
            >
              Add Weekly Monitoring Report (WMR)
            </Typography>

            <IconButton size="small" onClick={() => navigate(-1)}>
              <Icon name="close3" color={COLORS.ERROR} />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={0.4}>
            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Project Name:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {propProjectName || project.projectName}
                </span>
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap={isMobile ? "wrap" : "nowrap"}
              >
                <Typography
                  sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
                >
                  Week:
                </Typography>

                {weekStartDate && weekEndDate ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      value={`${formatDateForWeekHeader(weekStartDate)} - ${formatDateForWeekHeader(weekEndDate)}`}
                      onClick={() => setShowDatePicker(true)}
                      sx={{
                        width: isMobile ? "100%" : "200px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          height: "32px",
                          pr: 0.5,
                          fontFamily: FONT_FAMILY,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowDatePicker(true)}
                            >
                              <Icon name="calendar" color={COLORS.SECONDARY} />
                            </IconButton>
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                    />
                    <Button
                      size="small"
                      onClick={() => setShowDatePicker(true)}
                      sx={{ textTransform: "none", fontFamily: FONT_FAMILY }}
                    >
                      Change
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      borderRadius: 1,
                      px: 1.2,
                      py: 0.3,
                      gap: 0.6,
                      fontSize: TYPOGRAPHY.BODY_SMALL.fontSize,
                      color: COLORS.PRIMARY,
                      minWidth: 110,
                      justifyContent: "space-between",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: COLORS.SECONDARY,
                      },
                    }}
                    onClick={() => setShowDatePicker(true)}
                  >
                    {weekStartDate
                      ? formatDateForWeekHeader(weekStartDate)
                      : "DD/MM/YYYY - DD/MM/YYYY"}
                    <Icon name="calendar3" color={COLORS.SECONDARY} />
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Address:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.siteAddress || "N/A"}
                </span>
              </Typography>

              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                PMC:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {companies.find((c) => c._id === selectedCompany)?.name ||
                    "N/A"}
                </span>
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Developer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.developerName || "N/A"}
                </span>
              </Typography>

              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Engineer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.assignedEngineer?.name ||
                    project.assignedEngineerName ||
                    "Not Assigned"}
                </span>
              </Typography>
            </Box>
          </Box>

          {/* HEADER BOTTOM LINE */}
          <Box
            mt={1.5}
            height="1px"
            sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
          />
        </Box>

        {/* Show warning if week not selected */}
        {!weekStartDate && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please select a week date to start adding entries
          </Alert>
        )}

        {/* ================= 1. PROJECT DETAILS ================= */}
        {weekStartDate && (
          <>
            {/* ================= STAKEHOLDERS ================= */}
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 1,
                mt: 2,
                fontFamily: FONT_FAMILY,
                color: COLORS.TEXT,
              }}
            >
              Stakeholders
            </Typography>

            <Box
              display="flex"
              sx={{
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                py: 0.8,
                ...TYPOGRAPHY.H4,
                color: COLORS.TEXT,
                fontFamily: FONT_FAMILY,
              }}
            >
              <Box width="20%" px={1}>
                Role
              </Box>
              <Box width="30%" px={1}>
                Name
              </Box>
              <Box width="50%" px={1}>
                Description
              </Box>
            </Box>

            {project?.stakeholders?.map((stakeholder, i) => (
              <Box
                key={i}
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 0.8,
                }}
              >
                <Box
                  width="20%"
                  px={1}
                  sx={{
                    color: COLORS.SECONDARY,
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {stakeholder.role?.toUpperCase() || "N/A"}
                </Box>
                <Box
                  width="30%"
                  px={1}
                  sx={{
                    color: COLORS.SECONDARY,
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {stakeholder.name || "N/A"}
                </Box>
                <Box
                  width="50%"
                  px={1}
                  sx={{
                    color: COLORS.SECONDARY,
                    ...TYPOGRAPHY.BODY_LARGE,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {stakeholder.details || "N/A"}
                </Box>
              </Box>
            ))}

            {/* ================= PROJECT LAYOUT IMAGE ================= */}
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                mb: 1,
                mt: 2,
                fontFamily: FONT_FAMILY,
                color: COLORS.TEXT,
              }}
            >
              Project Layout Image
            </Typography>

            <Box
              sx={{
                border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}
            >
              {projectLayoutImagePreview ? (
                <Box>
                  <Box
                    component="img"
                    src={projectLayoutImagePreview}
                    alt="Project Layout"
                    sx={{
                      width: "100%",
                      maxHeight: "300px",
                      objectFit: "contain",
                      borderRadius: 1,
                      mb: 1,
                    }}
                  />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.SECONDARY }}
                    >
                      {projectLayoutImage?.name}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<Icon name="download1" size={16} />}
                        onClick={downloadProjectLayoutImage}
                      >
                        Download
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Icon name="trash" size={16} />}
                        onClick={removeProjectLayoutImage}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: COLORS.PRIMARY,
                      backgroundColor: "rgba(0, 0, 0, 0.02)",
                    },
                  }}
                  onClick={() =>
                    document.getElementById("project-layout-input").click()
                  }
                >
                  <Icon
                    name="upload"
                    size={32}
                    color={COLORS.LIGHT_SECONDARY}
                  />
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      mt: 1,
                      color: COLORS.SECONDARY,
                    }}
                  >
                    Click to upload project layout image
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      mt: 0.5,
                      color: COLORS.LIGHT_SECONDARY,
                    }}
                  >
                    Supported formats: JPG, PNG, GIF (Max 10MB)
                  </Typography>
                  <input
                    id="project-layout-input"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleProjectLayoutImageUpload}
                  />
                </Box>
              )}
            </Box>

            {/* ================= 2. WORK EXECUTED ================= */}
            <Typography
              sx={{
                ...TYPOGRAPHY.H3,
                color: COLORS.PRIMARY,
                mt: 4,
                mb: 1.5,
                fontFamily: FONT_FAMILY,
              }}
            >
              2. Work Executed During the Week:
            </Typography>

            {/* RCC Work */}
            <Box mb={2.5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  mb: 0.8,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                }}
              >
                RCC Work
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.rccWork}
                onChange={(value) =>
                  setWmrData((prev) => ({
                    ...prev,
                    workExecuted: { ...prev.workExecuted, rccWork: value },
                  }))
                }
                placeholder="Describe RCC work executed during the week..."
              />
            </Box>

            {/* Civil Works */}
            <Box mb={2.5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  mb: 0.8,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                }}
              >
                Civil Works
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.civilWorks}
                onChange={(value) =>
                  setWmrData((prev) => ({
                    ...prev,
                    workExecuted: { ...prev.workExecuted, civilWorks: value },
                  }))
                }
                placeholder="Describe civil works executed during the week..."
              />
            </Box>

            {/* Masonry / Finishing Works */}
            <Box mb={2.5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  mb: 0.8,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                }}
              >
                Masonry / Finishing Works
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.masonryFinishing}
                onChange={(value) =>
                  setWmrData((prev) => ({
                    ...prev,
                    workExecuted: {
                      ...prev.workExecuted,
                      masonryFinishing: value,
                    },
                  }))
                }
                placeholder="Describe masonry/finishing works executed during the week..."
              />
            </Box>

            {/* MEP Works */}
            <Box mb={2.5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  mb: 0.8,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                }}
              >
                MEP Works
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.mepWorks}
                onChange={(value) =>
                  setWmrData((prev) => ({
                    ...prev,
                    workExecuted: { ...prev.workExecuted, mepWorks: value },
                  }))
                }
                placeholder="Describe MEP works executed during the week..."
              />
            </Box>

            {/* External Development */}
            <Box mb={2.5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  mb: 0.8,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                }}
              >
                External Development
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.externalDevelopment}
                onChange={(value) =>
                  setWmrData((prev) => ({
                    ...prev,
                    workExecuted: {
                      ...prev.workExecuted,
                      externalDevelopment: value,
                    },
                  }))
                }
                placeholder="Describe external development work executed during the week..."
              />
            </Box>

            {/* ================= 3. WORK IN PROGRESS ================= */}
            <Typography
              sx={{
                ...TYPOGRAPHY.H3,
                color: COLORS.PRIMARY,
                mt: 4,
                mb: 1.5,
                fontFamily: FONT_FAMILY,
              }}
            >
              3. Work in Progress (WIP):
            </Typography>

            {/* Merged Work Progress Table */}
            <Box mb={3}>
              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 0.8,
                }}
              >
                <Box
                  flex={1}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Activity
                </Box>
                <Box
                  flex={1}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Location
                </Box>
                <Box
                  flex={1}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Status
                </Box>
                <Box
                  flex={1}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Remark
                </Box>
                <Box
                  width="120px"
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                    textAlign: "center",
                  }}
                >
                  Actions
                </Box>
              </Box>

              {/* Table Body */}
              {wmrData.workInProgress?.mergedProgress?.map((row, i) => (
                <Box
                  key={i}
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.activity}
                      onChange={(e) => {
                        const updatedProgress = [
                          ...wmrData.workInProgress.mergedProgress,
                        ];
                        updatedProgress[i].activity = e.target.value;
                        setWmrData((prev) => ({
                          ...prev,
                          workInProgress: {
                            mergedProgress: updatedProgress,
                          },
                        }));
                      }}
                      multiline
                      maxRows={3}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.location}
                      onChange={(e) => {
                        const updatedProgress = [
                          ...wmrData.workInProgress.mergedProgress,
                        ];
                        updatedProgress[i].location = e.target.value;
                        setWmrData((prev) => ({
                          ...prev,
                          workInProgress: {
                            mergedProgress: updatedProgress,
                          },
                        }));
                      }}
                      multiline
                      maxRows={3}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.status}
                      onChange={(e) => {
                        const updatedProgress = [
                          ...wmrData.workInProgress.mergedProgress,
                        ];
                        updatedProgress[i].status = e.target.value;
                        setWmrData((prev) => ({
                          ...prev,
                          workInProgress: {
                            mergedProgress: updatedProgress,
                          },
                        }));
                      }}
                      multiline
                      maxRows={3}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.remark}
                      onChange={(e) => {
                        const updatedProgress = [
                          ...wmrData.workInProgress.mergedProgress,
                        ];
                        updatedProgress[i].remark = e.target.value;
                        setWmrData((prev) => ({
                          ...prev,
                          workInProgress: {
                            mergedProgress: updatedProgress,
                          },
                        }));
                      }}
                      multiline
                      maxRows={3}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    width="120px"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNewProgressEntry({
                          activity: row.activity,
                          location: row.location,
                          status: row.status,
                          remark: row.remark,
                        });
                        setEditingEntry({
                          section: "progress",
                          index: i,
                          data: row,
                        });
                        setShowActivityForm(true);
                      }}
                      sx={{ color: COLORS.PRIMARY }}
                    >
                      <Icon name="Action3" size={16} color="#8CD47E" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteProgressEntry(i)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Add New Entry Button */}
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                startIcon={<Icon name="plus2" />}
                sx={addBtnStyle}
                onClick={() => {
                  setNewProgressEntry({
                    activity: "",
                    location: "",
                    status: "",
                    remark: "",
                  });
                  setShowActivityForm(true);
                }}
              >
                Add New Entry
              </Button>
            </Box>

            {/* Input Form */}
            {showActivityForm && (
              <Box
                py={2}
                sx={{
                  border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  borderRadius: "8px",
                  p: 2,
                  mt: 2,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.PRIMARY,
                      ...TYPOGRAPHY.H4,
                    }}
                  >
                    {editingEntry.section === "progress"
                      ? "Edit Entry"
                      : "Add New Progress Entry"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowActivityForm(false);
                      setNewProgressEntry({
                        activity: "",
                        location: "",
                        status: "",
                        remark: "",
                      });
                      setEditingEntry({
                        section: null,
                        index: null,
                        data: null,
                      });
                    }}
                    sx={{ color: COLORS.ERROR }}
                  >
                    <Icon name="close2" />
                  </IconButton>
                </Box>
                <Box display="flex" gap={2} mb={2}>
                  <Box flex={1}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Activity
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Activity"
                      size="small"
                      value={newProgressEntry.activity}
                      onChange={(e) =>
                        setNewProgressEntry({
                          ...newProgressEntry,
                          activity: e.target.value,
                        })
                      }
                      sx={inputStyle}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Location
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Location"
                      size="small"
                      value={newProgressEntry.location}
                      onChange={(e) =>
                        setNewProgressEntry({
                          ...newProgressEntry,
                          location: e.target.value,
                        })
                      }
                      sx={inputStyle}
                      multiline
                      maxRows={3}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={1} mb={2}>
                  <Box flex={1}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Status
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Status"
                      size="small"
                      value={newProgressEntry.status}
                      onChange={(e) =>
                        setNewProgressEntry({
                          ...newProgressEntry,
                          status: e.target.value,
                        })
                      }
                      sx={inputStyle}
                      multiline
                      maxRows={3}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Remark
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Remark"
                      size="small"
                      value={newProgressEntry.remark}
                      onChange={(e) =>
                        setNewProgressEntry({
                          ...newProgressEntry,
                          remark: e.target.value,
                        })
                      }
                      sx={inputStyle}
                      multiline
                      maxRows={3}
                    />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    startIcon={<Icon name="delete" />}
                    sx={{
                      ...addBtnStyle,
                      backgroundColor: "transparent",
                      color: COLORS.ERROR,
                      border: `1px solid ${COLORS.ERROR}`,
                      "&:hover": {
                        backgroundColor: `${COLORS.ERROR}15`,
                        border: `1px solid ${COLORS.ERROR}`,
                      }
                    }}
                    onClick={() => {
                      setNewProgressEntry({
                        activity: "",
                        location: "",
                        status: "",
                        remark: "",
                      });
                      showToast("info", "Fields Cleared", "The entry form has been reset.");
                    }}
                  >
                    Clear Fields
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={handleAddProgressEntry}
                  >
                    {editingEntry.section === "progress"
                      ? "Update Entry"
                      : "Add Entry"}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ================= 4. PLANNED VS ACTUAL PROGRESS ================= */}
            <Box mt={4}>
              {/* Title Row with Import Button */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
              >
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H3,
                    color: COLORS.PRIMARY,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  4. Planned vs Actual Progress:
                </Typography>

                <Box display="flex" gap={1}>
                  {isWPRImported ? (
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="close2" color={COLORS.ERROR} />}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        fontFamily: FONT_FAMILY,
                        color: COLORS.ERROR,
                        borderColor: COLORS.ERROR,
                        "&:hover": {
                          borderColor: COLORS.ERROR,
                          backgroundColor: "rgba(244, 67, 54, 0.04)",
                        },
                      }}
                      onClick={removeWPRImport}
                    >
                      Remove WPR Import
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="upload" />}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        fontFamily: FONT_FAMILY,
                        color: COLORS.SECONDARY,
                        borderColor: COLORS.SECONDARY,
                      }}
                      onClick={fetchWPRForImport}
                      disabled={isImporting}
                    >
                      {isImporting ? "Importing..." : "Import from WPR"}
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Show message if WPR imported */}
              {/* {showWPRImport && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  WPR data imported successfully! Review and edit the variance/reason column as needed.
                </Alert>
              )} */}

              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  py: 1.2,
                  ...TYPOGRAPHY.H4,
                  color: COLORS.TEXT,
                  fontFamily: FONT_FAMILY,
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  alignItems: "center",
                }}
              >
                <FlexCell
                  w="160px"
                  borderBottom="none"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Week Days
                </FlexCell>
                <FlexCell
                  flex={1}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Contractor/Vendor
                </FlexCell>
                <FlexCell
                  flex={1.4}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Targeted Activity Details
                </FlexCell>
                <FlexCell
                  w="100px"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Status
                </FlexCell>
                <FlexCell
                  flex={1.2}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Variance/Reason
                </FlexCell>
                <FlexCell
                  w="140px"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Expected Completion Date
                </FlexCell>
                <FlexCell
                  w="120px"
                  borderBottom="none"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Actions
                </FlexCell>
              </Box>

              {/* Table Body - Using WPR-like structure */}
              {weekDaysData &&
                weekDaysData.map((dayBlock, dayIndex) => (
                  <Box key={dayIndex}>
                    {/* Show entries only if they exist */}
                    {dayBlock.entries &&
                      dayBlock.entries.map((row, entryIndex) => (
                        <FlexRow key={entryIndex}>
                          {/* WEEK COLUMN */}
                          <FlexCell
                            w="160px"
                            sx={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {entryIndex === 0 && (
                              <Typography
                                sx={{
                                  ...TYPOGRAPHY.BODY_LARGE,
                                  color: COLORS.SECONDARY,
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {dayBlock.day.split(" ")[0]}
                                <br />
                                {dayBlock.formattedDate
                                  .split(" ")
                                  .slice(1)
                                  .join(" ")}
                              </Typography>
                            )}
                          </FlexCell>

                          {/* DATA COLUMNS */}
                          <FlexRowInner>
                            <FlexCell
                              flex={1}
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Typography
                                sx={{
                                  color: COLORS.SECONDARY,
                                  ...TYPOGRAPHY.BODY_LARGE,
                                  wordWrap: "break-word",
                                  lineHeight: 1.4,
                                  whiteSpace: "normal",
                                }}
                              >
                                {row.contractor}
                              </Typography>
                            </FlexCell>
                            <FlexCell
                              flex={1.4}
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Typography
                                sx={{
                                  color: COLORS.SECONDARY,
                                  ...TYPOGRAPHY.BODY_LARGE,
                                  wordWrap: "break-word",
                                  lineHeight: 1.4,
                                  whiteSpace: "normal",
                                }}
                              >
                                {row.activity}
                              </Typography>
                            </FlexCell>
                            <FlexCell
                              w="100px"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Typography
                                sx={{
                                  color: COLORS.SECONDARY,
                                  ...TYPOGRAPHY.BODY_LARGE,
                                  wordWrap: "break-word",
                                  lineHeight: 1.4,
                                  fontWeight:
                                    row.status === "OPEN" ? "bold" : "normal",
                                  color:
                                    row.status === "OPEN"
                                      ? COLORS.PRIMARY
                                      : COLORS.SECONDARY,
                                }}
                              >
                                {row.status || "OPEN"}
                              </Typography>
                            </FlexCell>
                            <FlexCell
                              flex={1.2}
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Typography
                                sx={{
                                  color: COLORS.SECONDARY,
                                  ...TYPOGRAPHY.BODY_LARGE,
                                  wordWrap: "break-word",
                                  lineHeight: 1.4,
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {row.variance || "-"}
                              </Typography>
                            </FlexCell>
                            <FlexCell
                              w="140px"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Typography
                                sx={{
                                  color: COLORS.SECONDARY,
                                  ...TYPOGRAPHY.BODY_LARGE,
                                  wordWrap: "break-word",
                                  lineHeight: 1.4,
                                  whiteSpace: "normal",
                                }}
                              >
                                {row.expectedCompletionDate
                                  ? format(
                                      row.expectedCompletionDate,
                                      "dd/MM/yyyy",
                                    )
                                  : "-"}
                              </Typography>
                            </FlexCell>
                          </FlexRowInner>

                          {/* ACTIONS - Edit and Delete buttons */}
                          <FlexCell
                            w="120px"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                // Open edit modal with pre-filled data
                                setNewEntry({ ...row });
                                setOpenDay(dayIndex);
                                setEditingEntry({
                                  section: "plannedActual",
                                  dayIndex: dayIndex,
                                  entryIndex: entryIndex,
                                  data: row,
                                });
                              }}
                              sx={{ color: COLORS.PRIMARY }}
                            >
                              <Icon name="Action3" size={16} color="#8CD47E" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeletePlannedVsActualEntry(
                                  dayIndex,
                                  entryIndex,
                                )
                              }
                              sx={{ color: COLORS.ERROR }}
                            >
                              <Icon name="close2" size={16} />
                            </IconButton>
                          </FlexCell>
                        </FlexRow>
                      ))}

                    {/* Show row with date and empty cells when no entries */}
                    {dayBlock.entries.length === 0 && (
                      <FlexRow>
                        <FlexCell
                          w="160px"
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.SECONDARY,
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {dayBlock.day.split(" ")[0]}
                            <br />
                            {dayBlock.formattedDate
                              .split(" ")
                              .slice(1)
                              .join(" ")}
                          </Typography>
                        </FlexCell>
                        <FlexRowInner>
                          <FlexCell
                            flex={1}
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_LARGE,
                                color: COLORS.LIGHT_SECONDARY,
                                fontStyle: "italic",
                              }}
                            >
                              No entries yet
                            </Typography>
                          </FlexCell>
                          <FlexCell
                            flex={1.4}
                            sx={{ display: "flex", alignItems: "center" }}
                          />
                          <FlexCell
                            w="100px"
                            sx={{ display: "flex", alignItems: "center" }}
                          />
                          <FlexCell
                            flex={1.2}
                            sx={{ display: "flex", alignItems: "center" }}
                          />
                          <FlexCell
                            w="140px"
                            sx={{ display: "flex", alignItems: "center" }}
                          />
                        </FlexRowInner>
                        <FlexCell
                          w="120px"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      </FlexRow>
                    )}

                    {/* Show add form if this day is open */}
                    {openDay === dayIndex && (
                      <Box
                        py={2}
                        sx={{
                          border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          borderRadius: "8px",
                          p: 2,
                          mt: 1,
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Typography
                            sx={{
                              fontFamily: FONT_FAMILY,
                              color: COLORS.PRIMARY,
                              ...TYPOGRAPHY.H4,
                            }}
                          >
                            {editingEntry.section === "plannedActual"
                              ? "Edit Entry"
                              : "Add New Entry"}{" "}
                            for {dayBlock.formattedDate}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setOpenDay(null)}
                            sx={{ color: COLORS.ERROR }}
                          >
                            <Icon name="close2" />
                          </IconButton>
                        </Box>
                        <Box display="flex" gap={2} mb={2}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Contractor / Vendor"
                            value={newEntry.contractor}
                            onChange={(e) =>
                              setNewEntry({
                                ...newEntry,
                                contractor: e.target.value,
                              })
                            }
                            sx={inputStyle}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Targeted Activity Details"
                            value={newEntry.activity}
                            onChange={(e) =>
                              setNewEntry({
                                ...newEntry,
                                activity: e.target.value,
                              })
                            }
                            sx={inputStyle}
                          />
                        </Box>
                        <Box display="flex" gap={2}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={newEntry.status || "OPEN"}
                            onChange={(e) =>
                              setNewEntry({
                                ...newEntry,
                                status: e.target.value,
                              })
                            }
                            sx={inputStyle}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    maxHeight: 200,
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="OPEN">Open</MenuItem>
                            <MenuItem value="CLOSED">Closed</MenuItem>
                          </TextField>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Variance / Reason"
                            value={newEntry.variance}
                            onChange={(e) =>
                              setNewEntry({
                                ...newEntry,
                                variance: e.target.value,
                              })
                            }
                            sx={inputStyle}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            placeholder="Expected Completion Date"
                            value={
                              newEntry.expectedCompletionDate
                                ? format(
                                    newEntry.expectedCompletionDate,
                                    "yyyy-MM-dd",
                                  )
                                : ""
                            }
                            onChange={(e) =>
                              setNewEntry({
                                ...newEntry,
                                expectedCompletionDate: e.target.value
                                  ? new Date(e.target.value)
                                  : null,
                              })
                            }
                            sx={inputStyle}
                          />
                        </Box>
                        <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                          <Button
                            variant="outlined"
                            startIcon={<Icon name="delete" />}
                            sx={{
                              ...addBtnStyle,
                              backgroundColor: "transparent",
                              color: COLORS.ERROR,
                              border: `1px solid ${COLORS.ERROR}`,
                              "&:hover": {
                                backgroundColor: `${COLORS.ERROR}15`,
                                border: `1px solid ${COLORS.ERROR}`,
                              }
                            }}
                            onClick={() => {
                              clearNewEntryPersistence();
                              setNewEntry({
                                contractor: "",
                                activity: "",
                                status: "OPEN",
                                variance: "",
                                expectedCompletionDate: null,
                              });
                              showToast("info", "Fields Cleared", "The entry form has been reset.");
                            }}
                          >
                            Clear Fields
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<Icon name="plus2" />}
                            sx={addBtnStyle}
                            onClick={() =>
                              handleAddPlannedVsActualEntry(dayIndex)
                            }
                          >
                            {editingEntry.section === "plannedActual"
                              ? "Update Entry"
                              : "Add New Entry"}
                          </Button>
                        </Box>
                      </Box>
                    )}

                    {/* Always show "Add New Entry" button at the end of each day's section */}
                    <Box display="flex" justifyContent="flex-end" mt={1} mb={2}>
                      <Button
                        size="small"
                        startIcon={<Icon name="plus2" />}
                        sx={{
                          backgroundColor: COLORS.SECONDARY,
                          color: "#fff",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: COLORS.PRIMARY,
                          },
                        }}
                        onClick={() =>
                          setOpenDay(openDay === dayIndex ? null : dayIndex)
                        }
                      >
                        {`Add New Entry for ${dayBlock.day.split(" ")[0]}`}
                      </Button>
                    </Box>

                    {/* DAY SEPARATOR */}
                    <Box
                      mt={2}
                      height="1px"
                      sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
                    />
                  </Box>
                ))}
            </Box>

            {/* ================= 5. MANPOWER DEPLOYMENT ================= */}
            <Box mt={5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 1.5,
                }}
              >
                5. Manpower Deployment:
              </Typography>

              {/* Header Row */}
              <Box
                display="flex"
                gap={0.5}
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  pb: 1,
                  mb: 2,
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <Box
                  width="180px"
                  pl={2}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.TEXT,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Labour Type
                </Box>
                {wmrData.manpowerDeployment?.dates?.map((date, i) => (
                  <Box
                    key={i}
                    width="90px"
                    textAlign="center"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.TEXT,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {date}
                  </Box>
                ))}
                <Box
                  width="90px"
                  textAlign="center"
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.TEXT,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Average
                </Box>
                <Box
                  width="90px"
                  textAlign="center"
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.TEXT,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Total
                </Box>
                <Box
                  width="60px"
                  textAlign="center"
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.TEXT,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Actions
                </Box>
              </Box>

              {/* Data Rows */}
              {wmrData.manpowerDeployment?.laborTypes?.map(
                (labor, typeIndex) => (
                  <Box key={typeIndex} display="flex" gap={0.5} sx={{ mb: 2 }}>
                    <Box
                      width="180px"
                      sx={cellStyle}
                      display="flex"
                      alignItems="center"
                    >
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={labor.type}
                        onChange={(e) =>
                          handleLaborTypeChange(typeIndex, e.target.value)
                        }
                        inputProps={{
                          style: { padding: "8px 12px", height: "32px" },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "44px",
                            borderRadius: "8px",
                            "& fieldset": {
                              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                            },
                          },
                        }}
                      />
                    </Box>
                    {labor?.data?.map((value, dayIndex) => (
                      <Box key={dayIndex} width="90px" sx={cellStyle}>
                        <TextField
                          type="text"
                          variant="outlined"
                          size="small"
                          placeholder="0"
                          value={value}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) =>
                            handleNumericInput(e, (val) =>
                              handleManpowerChange(typeIndex, dayIndex, val),
                            )
                          }
                          onCut={(e) =>
                            handleCutOperation(e, () =>
                              handleManpowerChange(typeIndex, dayIndex, ""),
                            )
                          }
                          inputProps={{
                            style: {
                              textAlign: "center",
                              padding: "4px 8px",
                              height: "24px",
                            },
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              height: "44px",
                              borderRadius: "8px",
                              "& fieldset": { border: "none" },
                            },
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                          }}
                        />
                      </Box>
                    ))}
                    <Box
                      width="90px"
                      sx={{
                        ...cellStyle,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          color: COLORS.SECONDARY,
                          fontFamily: FONT_FAMILY,
                          textAlign: "center",
                        }}
                      >
                        {manpowerValues?.averages?.[labor.type] || 0}
                      </Typography>
                    </Box>
                    <Box
                      width="90px"
                      sx={{
                        ...cellStyle,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          color: COLORS.SECONDARY,
                          fontFamily: FONT_FAMILY,
                          textAlign: "center",
                        }}
                      >
                        {manpowerValues?.totals?.[labor.type] || 0}
                      </Typography>
                    </Box>
                    <Box
                      width="60px"
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteLaborType(typeIndex)}
                        sx={{ color: COLORS.ERROR }}
                      >
                        <Icon name="close2" size={16} />
                      </IconButton>
                    </Box>
                  </Box>
                ),
              )}

              {/* Add Button */}
              <Box display="flex" justifyContent="flex-end" mt={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<Icon name="plus2" />}
                  sx={addBtnStyle}
                  onClick={handleAddLaborType}
                >
                  Add New Entry
                </Button>
              </Box>

              {/* Total Row - CENTER ALIGNED */}
              <Box
                mt={3}
                pt={2}
                display="flex"
                gap={0.5}
                sx={{ borderTop: `2px solid ${COLORS.LIGHT_SECONDARY}` }}
              >
                <Box
                  width="180px"
                  sx={{ ...cellStyle, display: "flex", alignItems: "center" }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.PRIMARY,
                      fontFamily: FONT_FAMILY,
                      pl: 2,
                    }}
                  >
                    Total
                  </Typography>
                </Box>
                {manpowerValues?.columnTotals?.map((total, i) => (
                  <Box
                    key={i}
                    width="90px"
                    sx={{
                      ...cellStyle,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.SECONDARY,
                        fontFamily: FONT_FAMILY,
                        textAlign: "center",
                      }}
                    >
                      {total}
                    </Typography>
                  </Box>
                ))}
                <Box
                  width="90px"
                  sx={{
                    ...cellStyle,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      fontFamily: FONT_FAMILY,
                      textAlign: "center",
                    }}
                  >
                    {manpowerValues?.grandAverage || 0}
                  </Typography>
                </Box>
                <Box
                  width="90px"
                  sx={{
                    ...cellStyle,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      fontFamily: FONT_FAMILY,
                      textAlign: "center",
                    }}
                  >
                    {manpowerValues?.grandTotal || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ================= 6. MACHINERY & EQUIPMENTS ================= */}
            <Box mt={5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 1.5,
                }}
              >
                6. Machinery & Equipments:
              </Typography>

              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  pb: 1,
                  mb: 2,
                  ...TYPOGRAPHY.H4,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.TEXT,
                }}
              >
                <Box flex={2}>Type of Equipment</Box>
                <Box width="120px" textAlign="center">
                  Quantity
                </Box>
                <Box width="140px" textAlign="center">
                  Working Hrs
                </Box>
                <Box flex={1.2}>Purpose</Box>
                <Box width="60px" textAlign="center">
                  Actions
                </Box>
              </Box>

              {/* Table Rows - EDITABLE PRE-FILLED DATA */}
              {wmrData.machineryEquipment?.map((machine, index) => (
                <Box
                  key={index}
                  display="flex"
                  gap={2}
                  mb={2}
                  alignItems="center"
                >
                  <Box
                    flex={2}
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={machine.equipment}
                      onChange={(e) =>
                        handleMachineChange(index, "equipment", e.target.value)
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    width="120px"
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      value={machine.quantity}
                      onChange={(e) =>
                        handleMachineChange(index, "quantity", e.target.value)
                      }
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                      inputProps={{ style: { textAlign: "center" } }}
                    />
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      value={machine.hours}
                      onChange={(e) =>
                        handleMachineChange(index, "hours", e.target.value)
                      }
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                      inputProps={{ style: { textAlign: "center" } }}
                    />
                  </Box>
                  <Box
                    flex={1.2}
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={machine.utilization || ""}
                      placeholder="-"
                      onChange={(e) =>
                        handleMachineChange(
                          index,
                          "utilization",
                          e.target.value,
                        )
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    width="60px"
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMachine(index)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {/* Add Entry Form */}
              {showMachineEntry && (
                <Box
                  mt={3}
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add New Machine Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowMachineEntry(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={2}>
                      <Typography sx={labelStyle}>Type of Equipment</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Equipment Name"
                        value={newMachine.equipment}
                        onChange={(e) =>
                          setNewMachine({
                            ...newMachine,
                            equipment: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box width="120px">
                      <Typography sx={labelStyle}>Quantity</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="0"
                        type="number"
                        value={newMachine.quantity}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setNewMachine({
                            ...newMachine,
                            quantity: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box width="140px">
                      <Typography sx={labelStyle}>Working Hrs</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="0"
                        type="number"
                        value={newMachine.hours}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setNewMachine({
                            ...newMachine,
                            hours: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1.2}>
                      <Typography sx={labelStyle}>Purpose</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="-"
                        value={newMachine.utilization || ""}
                        onChange={(e) =>
                          setNewMachine({
                            ...newMachine,
                            utilization: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" mt={1.5} gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={{
                        ...addBtnStyle,
                        backgroundColor: "transparent",
                        color: COLORS.ERROR,
                        border: `1px solid ${COLORS.ERROR}`,
                        "&:hover": {
                          backgroundColor: `${COLORS.ERROR}15`,
                          border: `1px solid ${COLORS.ERROR}`,
                        }
                      }}
                      onClick={() => {
                        setNewMachine({
                          equipment: "",
                          quantity: "",
                          hours: "",
                          utilization: "",
                        });
                        showToast("info", "Fields Cleared", "The machinery form has been reset.");
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={handleAddMachine}
                    >
                      Add Entry
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Add Button */}
              {!showMachineEntry && (
                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={() => setShowMachineEntry(true)}
                  >
                    Add New Entry
                  </Button>
                </Box>
              )}
            </Box>

            {/* ================= 7. MATERIAL STATUS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  mb: 1.5,
                  fontFamily: FONT_FAMILY,
                }}
              >
                7. Material Status:
              </Typography>

              {/* Table Header */}
              <Box
                display="flex"
                gap={2}
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 1,
                  mb: 2,
                  ...TYPOGRAPHY.H4,
                  color: COLORS.TEXT,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <Box flex={2}>Material Description</Box>
                <Box width="140px" textAlign="center">
                  Material Received
                </Box>
                <Box width="140px" textAlign="center">
                  Material Consumed
                </Box>
                <Box width="140px" textAlign="center">
                  Balance At Site
                </Box>
                <Box width="60px" textAlign="center">
                  Actions
                </Box>
              </Box>

              {/* Table Rows - EDITABLE PRE-FILLED DATA */}
              {wmrData.materialStatus?.map((material, index) => (
                <Box
                  key={index}
                  display="flex"
                  gap={2}
                  mb={2}
                  alignItems="center"
                >
                  <Box
                    flex={2}
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={material.description}
                      onChange={(e) =>
                        handleMaterialChange(
                          index,
                          "description",
                          e.target.value,
                        )
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      value={material.received}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        handleMaterialChange(index, "received", e.target.value)
                      }
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                      inputProps={{ style: { textAlign: "center" } }}
                    />
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      value={material.consumed}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        handleMaterialChange(index, "consumed", e.target.value)
                      }
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                      inputProps={{ style: { textAlign: "center" } }}
                    />
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: parseFloat(material.balance) < 0 ? COLORS.ERROR : COLORS.SECONDARY,
                        textAlign: "center",
                        fontWeight: parseFloat(material.balance) < 0 ? 600 : 400,
                      }}
                    >
                      {material.balance}
                    </Typography>
                  </Box>
                  <Box
                    width="60px"
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMaterial(index)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {/* Add Entry Form */}
              {showMaterialEntry && (
                <Box
                  mt={3}
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add New Material Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowMaterialEntry(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2} alignItems="flex-end">
                    <Box flex={2}>
                      <Typography sx={labelStyle}>
                        Material Description
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="-"
                        size="small"
                        value={newMaterial.description}
                        onChange={(e) =>
                          setNewMaterial({
                            ...newMaterial,
                            description: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box width="140px">
                      <Typography sx={{ ...labelStyle, whiteSpace: "nowrap" }}>
                        Material Received
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        type="number"
                        value={newMaterial.received}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setNewMaterial({
                            ...newMaterial,
                            received: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box width="140px">
                      <Typography sx={{ ...labelStyle, whiteSpace: "nowrap" }}>
                        Material Consumed
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        type="number"
                        value={newMaterial.consumed}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setNewMaterial({
                            ...newMaterial,
                            consumed: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box width="140px">
                      <Typography sx={{ ...labelStyle, whiteSpace: "nowrap" }}>
                        Balance At Site
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        value={newMaterial.balance}
                        disabled
                        sx={{
                          ...inputStyle,
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: COLORS.SECONDARY,
                            textAlign: "center",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" mt={1.5} gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={{
                        ...addBtnStyle,
                        backgroundColor: "transparent",
                        color: COLORS.ERROR,
                        border: `1px solid ${COLORS.ERROR}`,
                        "&:hover": {
                          backgroundColor: `${COLORS.ERROR}15`,
                          border: `1px solid ${COLORS.ERROR}`,
                        }
                      }}
                      onClick={() => {
                        setNewMaterial({
                          description: "",
                          received: "",
                          consumed: "",
                          balance: "",
                        });
                        showToast("info", "Fields Cleared", "The material form has been reset.");
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={handleAddMaterial}
                    >
                      Add Entry
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Add Button */}
              {!showMaterialEntry && (
                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={() => setShowMaterialEntry(true)}
                  >
                    Add New Entry
                  </Button>
                </Box>
              )}
            </Box>

            {/* ================= 8. QUALITY CONTROL ACTIVITIES ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  mb: 1.5,
                  fontFamily: FONT_FAMILY,
                }}
              >
                8. Quality Control Activities:
              </Typography>

              {/* Table Header */}
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 1,
                  mb: 2,
                  ...TYPOGRAPHY.H4,
                  color: COLORS.TEXT,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <Box flex={2.2} pl={2}>
                  Inspection Carried Out
                </Box>
                <Box width="140px" textAlign="center">
                  Test Conducted
                </Box>
                <Box flex={1.8} pl={2}>
                  NCR / Observations
                </Box>
                <Box width="120px" textAlign="center">
                  Status
                </Box>
                <Box width="60px" textAlign="center">
                  Actions
                </Box>
              </Box>

              {/* Table Rows - EDITABLE PRE-FILLED DATA */}
              {wmrData.qualityControl?.map((row, index) => (
                <Box
                  key={index}
                  display="flex"
                  gap={2}
                  mb={2}
                  alignItems="center"
                >
                  <Box
                    flex={2.2}
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.inspection}
                      onChange={(e) =>
                        handleQualityChange(index, "inspection", e.target.value)
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    width="140px"
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="0"
                      value={row.test}
                      onChange={(e) =>
                        handleNumericInput(e, (val) =>
                          handleQualityChange(index, "test", val),
                        )
                      }
                      onCut={(e) =>
                        handleCutOperation(e, () =>
                          handleQualityChange(index, "test", ""),
                        )
                      }
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                      inputProps={{
                        style: { textAlign: "center" },
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                    />
                  </Box>
                  <Box
                    flex={1.8}
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="-"
                      value={row.observation}
                      onChange={(e) =>
                        handleQualityChange(
                          index,
                          "observation",
                          e.target.value,
                        )
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    width="120px"
                    sx={{
                      ...machineCellStyle,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      value={row.status || "OPEN"}
                      onChange={(e) =>
                        handleQualityChange(index, "status", e.target.value)
                      }
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          "& fieldset": { border: "none" },
                        },
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="OPEN">Open</MenuItem>
                      <MenuItem value="CLOSED">Closed</MenuItem>
                    </TextField>
                  </Box>
                  <Box
                    width="60px"
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteQuality(index)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {/* Add Entry Form */}
              {showQualityEntry && (
                <Box
                  mt={3}
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add New Quality Control Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowQualityEntry(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={2.2}>
                      <Typography sx={labelStyle}>
                        Inspection Carried Out
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Inspection Description"
                        value={newQuality.inspection}
                        onChange={(e) =>
                          setNewQuality({
                            ...newQuality,
                            inspection: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box width="140px">
                      <Typography sx={labelStyle}>Test Conducted</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="0"
                        value={newQuality.test}
                        onChange={(e) =>
                          handleNumericInput(e, (val) =>
                            setNewQuality({ ...newQuality, test: val }),
                          )
                        }
                        onCut={(e) =>
                          handleCutOperation(e, () =>
                            setNewQuality({ ...newQuality, test: "" }),
                          )
                        }
                        sx={inputStyle}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                    </Box>
                    <Box flex={1.8}>
                      <Typography sx={labelStyle}>
                        NCR / Observations
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="-"
                        size="small"
                        value={newQuality.observation}
                        onChange={(e) =>
                          setNewQuality({
                            ...newQuality,
                            observation: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box width="120px">
                      <Typography sx={labelStyle}>Status</Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={newQuality.status || "OPEN"}
                        onChange={(e) =>
                          setNewQuality({
                            ...newQuality,
                            status: e.target.value,
                          })
                        }
                        sx={inputStyle}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="OPEN">Open</MenuItem>
                        <MenuItem value="CLOSED">Closed</MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" mt={1.5} gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={{
                        ...addBtnStyle,
                        backgroundColor: "transparent",
                        color: COLORS.ERROR,
                        border: `1px solid ${COLORS.ERROR}`,
                        "&:hover": {
                          backgroundColor: `${COLORS.ERROR}15`,
                          border: `1px solid ${COLORS.ERROR}`,
                        }
                      }}
                      onClick={() => {
                        setNewQuality({
                          inspection: "",
                          test: "",
                          observation: "",
                          status: "OPEN",
                        });
                        showToast("info", "Fields Cleared", "The quality form has been reset.");
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={handleAddQuality}
                    >
                      Add Entry
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Add Button */}
              {!showQualityEntry && (
                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={() => setShowQualityEntry(true)}
                  >
                    Add New Entry
                  </Button>
                </Box>
              )}
            </Box>

            {/* ================= 9. SAFETY & HSE STATUS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                  mb: 2,
                }}
              >
                9. Safety & HSE Status:
              </Typography>

              {/* Safety Compliance */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Safety Compliance
                </Typography>
                <RichTextEditor
                  value={wmrData.safetyHSE.safetyCompliance}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      safetyHSE: { ...prev.safetyHSE, safetyCompliance: value },
                    }))
                  }
                  placeholder="Describe safety compliance activities..."
                />
              </Box>

              {/* Toolbox Talks */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Toolbox Talks
                </Typography>
                <RichTextEditor
                  value={wmrData.safetyHSE.toolboxTalks}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      safetyHSE: { ...prev.safetyHSE, toolboxTalks: value },
                    }))
                  }
                  placeholder="Describe toolbox talks conducted..."
                />
              </Box>

              {/* Accidents / Near Misses */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Accidents / Near Misses
                </Typography>
                <RichTextEditor
                  value={wmrData.safetyHSE.accidentsNearMisses}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      safetyHSE: {
                        ...prev.safetyHSE,
                        accidentsNearMisses: value,
                      },
                    }))
                  }
                  placeholder="Report any accidents or near misses..."
                />
              </Box>
            </Box>

            {/* ================= 10. ISSUES / CONSTRAINTS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                  mb: 2,
                }}
              >
                10. Issues / Constraints:
              </Typography>

              {/* Technical */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Technical
                </Typography>
                <RichTextEditor
                  value={wmrData.issuesConstraints.technical}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      issuesConstraints: {
                        ...prev.issuesConstraints,
                        technical: value,
                      },
                    }))
                  }
                  placeholder="Describe technical issues/constraints..."
                />
              </Box>

              {/* Material */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Material
                </Typography>
                <RichTextEditor
                  value={wmrData.issuesConstraints.material}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      issuesConstraints: {
                        ...prev.issuesConstraints,
                        material: value,
                      },
                    }))
                  }
                  placeholder="Describe material issues/constraints..."
                />
              </Box>

              {/* Manpower */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Manpower
                </Typography>
                <RichTextEditor
                  value={wmrData.issuesConstraints.manpower}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      issuesConstraints: {
                        ...prev.issuesConstraints,
                        manpower: value,
                      },
                    }))
                  }
                  placeholder="Describe manpower issues/constraints..."
                />
              </Box>

              {/* Design/Approval */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Design/Approval
                </Typography>
                <RichTextEditor
                  value={wmrData.issuesConstraints.designApproval}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      issuesConstraints: {
                        ...prev.issuesConstraints,
                        designApproval: value,
                      },
                    }))
                  }
                  placeholder="Describe design/approval issues/constraints..."
                />
              </Box>
            </Box>

            {/* ================= 11. INSTRUCTIONS / DECISIONS FROM VISITOR ================= */}
            <Typography
              sx={{
                ...TYPOGRAPHY.H3,
                color: COLORS.PRIMARY,
                mt: 4,
                mb: 1.5,
                fontFamily: FONT_FAMILY,
              }}
            >
              11. Instructions / Decisions from Visitor:
            </Typography>

            {/* Visitor Instructions Table */}
            <Box mb={3}>
              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 0.8,
                }}
              >
                <Box
                  width="120px"
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Date
                </Box>
                <Box
                  flex={1.5}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Visitor Name
                </Box>
                <Box
                  flex={1.2}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Designation
                </Box>
                <Box
                  flex={2}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Instruction / Decision
                </Box>
                <Box
                  flex={1.5}
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                  }}
                >
                  Remark
                </Box>
                <Box
                  width="120px"
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.TEXT,
                    textAlign: "center",
                  }}
                >
                  Actions
                </Box>
              </Box>

              {/* Table Body */}
              {wmrData.instructionsDecisions?.map((row, i) => (
                <Box
                  key={i}
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    width="120px"
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "14px", wordWrap: "break-word" }}
                    >
                      {row.date ? format(row.date, "dd/MM/yyyy") : ""}
                    </Typography>
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        wordWrap: "break-word",
                        lineHeight: 1.4,
                      }}
                    >
                      {row.visitorName || "-"}
                    </Typography>
                  </Box>
                  <Box
                    flex={1.2}
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        wordWrap: "break-word",
                        lineHeight: 1.4,
                      }}
                    >
                      {row.designation || "-"}
                    </Typography>
                  </Box>
                  <Box
                    flex={2}
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        wordWrap: "break-word",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {row.instruction || "-"}
                    </Typography>
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.SECONDARY,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        wordWrap: "break-word",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {row.remark || "-"}
                    </Typography>
                  </Box>
                  <Box
                    width="120px"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleEditVisitor(i)}
                      sx={{ color: COLORS.PRIMARY }}
                    >
                      <Icon name="Action3" size={16} color="#8CD47E" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteVisitor(i)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Add New Entry Button */}
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                startIcon={<Icon name="plus2" />}
                sx={addBtnStyle}
                onClick={() => setShowVisitorEntry(true)}
              >
                Add New Entry
              </Button>
            </Box>

            {/* Input Form */}
            {showVisitorEntry && (
              <Box
                py={2}
                sx={{
                  border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  borderRadius: "8px",
                  p: 2,
                  mt: 2,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.PRIMARY,
                      ...TYPOGRAPHY.H4,
                    }}
                  >
                    {editingVisitor !== null
                      ? "Edit Visitor Entry"
                      : "Add New Visitor Entry"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowVisitorEntry(false);
                      setEditingVisitor(null);
                      setShowOtherConsultantInput(false);
                      setNewVisitor({
                        date: new Date(),
                        visitorName: "",
                        designation: "",
                        otherDesignation: "",
                        instruction: "",
                        remark: "",
                      });
                    }}
                    sx={{ color: COLORS.ERROR }}
                  >
                    <Icon name="close2" />
                  </IconButton>
                </Box>

                {/* First Row - Date, Visitor Name, Designation */}
                <Box display="flex" gap={2} mb={2}>
                  <Box width="150px">
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Date
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={format(newVisitor.date, "yyyy-MM-dd")}
                      onChange={(e) =>
                        setNewVisitor({
                          ...newVisitor,
                          date: new Date(e.target.value),
                        })
                      }
                      sx={inputStyle}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Visitor Name
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter visitor name"
                      size="small"
                      value={newVisitor.visitorName}
                      onChange={(e) =>
                        setNewVisitor({
                          ...newVisitor,
                          visitorName: e.target.value,
                        })
                      }
                      sx={inputStyle}
                    />
                  </Box>
                  <Box width="200px">
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Designation
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={newVisitor.designation}
                      onChange={(e) => handleDesignationChange(e.target.value)}
                      sx={inputStyle}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="Structural Consultant">
                        Structural Consultant
                      </MenuItem>
                      <MenuItem value="MEP Consultant">MEP Consultant</MenuItem>
                      <MenuItem value="Architect">Architect</MenuItem>
                      <MenuItem value="Interior Designer">
                        Interior Designer
                      </MenuItem>
                      <MenuItem value="Developer / Builder">
                        Developer / Builder
                      </MenuItem>
                      <MenuItem value="Individual">Individual</MenuItem>
                      <MenuItem value="Other Consultant">
                        Other Consultant
                      </MenuItem>
                    </TextField>
                  </Box>
                </Box>

                {/* Second Row - Other Consultant (conditional) */}
                {showOtherConsultantInput && (
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.PRIMARY,
                          mb: 0.5,
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        Specify Other Consultant
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter consultant designation"
                        size="small"
                        value={newVisitor.otherDesignation}
                        onChange={(e) =>
                          setNewVisitor({
                            ...newVisitor,
                            otherDesignation: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                )}

                {/* Third Row - Instruction / Decision */}
                <Box display="flex" gap={2} mb={2}>
                  <Box flex={1}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Instruction / Decision
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter instruction or decision"
                      size="small"
                      value={newVisitor.instruction}
                      onChange={(e) =>
                        setNewVisitor({
                          ...newVisitor,
                          instruction: e.target.value,
                        })
                      }
                      multiline
                      maxRows={3}
                      sx={inputStyle}
                    />
                  </Box>
                </Box>

                {/* Fourth Row - Remark */}
                <Box display="flex" gap={1} mb={2}>
                  <Box flex={1}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mb: 0.5,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Remark
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter remark"
                      size="small"
                      value={newVisitor.remark}
                      onChange={(e) =>
                        setNewVisitor({ ...newVisitor, remark: e.target.value })
                      }
                      multiline
                      maxRows={3}
                      sx={inputStyle}
                    />
                  </Box>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    startIcon={<Icon name="delete" />}
                    sx={{
                      ...addBtnStyle,
                      backgroundColor: "transparent",
                      color: COLORS.ERROR,
                      border: `1px solid ${COLORS.ERROR}`,
                      "&:hover": {
                        backgroundColor: `${COLORS.ERROR}15`,
                        border: `1px solid ${COLORS.ERROR}`,
                      }
                    }}
                    onClick={() => {
                      setNewVisitor({
                        date: new Date(),
                        visitorName: "",
                        designation: "",
                        otherDesignation: "",
                        instruction: "",
                        remark: "",
                      });
                      showToast("info", "Fields Cleared", "The visitor form has been reset.");
                    }}
                  >
                    Clear Fields
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={handleAddVisitor}
                  >
                    {editingVisitor !== null ? "Update Entry" : "Add Entry"}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ================= 12. REPORTS ================= */}
            <Box mt={4}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ cursor: "pointer" }}
                onClick={() => setOpenReports(!openReports)}
              >
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H3,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  12. Reports
                </Typography>
                <Icon name={openReports ? "chevronUp" : "chevronDown"} />
              </Box>

              {openReports && (
                <Box mt={2}>
                  {uploadedReports.length > 0 && (
                    <Box>
                      {uploadedReports?.map((file, index) => (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                            borderRadius: "8px",
                            px: 2,
                            py: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: FONT_FAMILY,
                              color: COLORS.PRIMARY,
                            }}
                          >
                            {file.name}
                          </Typography>
                          <Box display="flex" gap={1}>
                            <IconButton
                              onClick={() => {
                                if (file instanceof File) {
                                  // Newly uploaded file - use createObjectURL
                                  window.open(URL.createObjectURL(file));
                                } else if (file.path) {
                                  // File from database - open via server URL
                                  window.open(
                                    `${url}${file.path.replace(/\\/g, "/")}`,
                                  );
                                }
                              }}
                            >
                              <Icon name="Action1" />
                            </IconButton>
                            <IconButton
                              onClick={() => handleRemoveReport(index)}
                            >
                              <Icon name="close2" color={COLORS.ERROR} />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                  <Box mb={2}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                    >
                      Upload Reports
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={handleReportUpload}
                      />
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 13. DECISION & DISCUSSION RECORDS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                  mb: 2,
                }}
              >
                13. Decision & Discussion Records:
              </Typography>
              <RichTextEditor
                value={wmrData.decisionDiscussion}
                onChange={(value) =>
                  setWmrData((prev) => ({ ...prev, decisionDiscussion: value }))
                }
                placeholder="Record decisions and discussions..."
              />
            </Box>

            {/* ================= 14. PLAN FOR NEXT WEEK ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                  mb: 2,
                }}
              >
                14. Plan for Next Week
              </Typography>

              {/* Target Activities */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Target Activities
                </Typography>
                <RichTextEditor
                  value={wmrData.planNextWeek.targetActivities}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      planNextWeek: {
                        ...prev.planNextWeek,
                        targetActivities: value,
                      },
                    }))
                  }
                  placeholder="Describe target activities for next week..."
                />
              </Box>

              {/* Resource Planning */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Resource Planning
                </Typography>
                <RichTextEditor
                  value={wmrData.planNextWeek.resourcePlanning}
                  onChange={(value) =>
                    setWmrData((prev) => ({
                      ...prev,
                      planNextWeek: {
                        ...prev.planNextWeek,
                        resourcePlanning: value,
                      },
                    }))
                  }
                  placeholder="Describe resource planning for next week..."
                />
              </Box>
            </Box>

            {/* ================= 15. PHOTOGRAPHIC PROGRESS ================= */}
            <Box mt={4}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H3,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  15. Photographic Progress
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<Icon name="plus2" />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontFamily: FONT_FAMILY,
                    backgroundColor: COLORS.SECONDARY,
                    color: "#fff",
                    px: 2.5,
                    "&:hover": {
                      backgroundColor: COLORS.SECONDARY,
                    },
                  }}
                  onClick={() => setShowPhotoUploadDialog(true)}
                  disabled={uploadedPhotos.length >= 4}
                >
                  Add Photo
                </Button>
              </Box>
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                {uploadedPhotos?.map((photo, index) => (
                  <Box key={index} sx={{ position: "relative" }}>
                    {/* Photo Display */}
                    <img
                      src={photo.imageUrl || URL.createObjectURL(photo.file)}
                      alt="progress"
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "300px",
                        objectFit: "contain",
                      }}
                    />

                    {/* Close Button at Top Right */}
                    <IconButton
                      size="small"
                      onClick={() => removePhoto(index)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "#fff",
                        color: COLORS.ERROR,
                        "&:hover": { backgroundColor: "#ffebee" },
                      }}
                    >
                      <Icon name="close2" />
                    </IconButton>

                    {/* Photo Info */}
                    <Box sx={{ p: 1 }}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_SMALL,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.TEXT,
                          fontWeight: "medium",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {photo.title || photo.name}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Empty State */}
              {uploadedPhotos.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Icon name="image" size={48} color={COLORS.LIGHT_SECONDARY} />
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.LIGHT_SECONDARY,
                      mt: 2,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    No photos added yet
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.LIGHT_SECONDARY,
                      mt: 1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    Click "Add Photo with Annotations" to start adding photos
                    with drawing tools
                  </Typography>
                </Box>
              )}

              {/* Photo Upload Dialog */}
              <PhotoUploadWithDrawing
                open={showPhotoUploadDialog}
                onClose={() => setShowPhotoUploadDialog(false)}
                onSave={handlePhotoUploadWithDrawing}
                maxPhotos={4}
                persistenceKey={`wmr-photo-draft-${persistenceKey}`}
              />
            </Box>

            {/* ================= SUBMIT BUTTON ================= */}
            <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={3}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Icon name="delete" />}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "10px",
                  fontFamily: FONT_FAMILY,
                  color: COLORS.ERROR,
                  borderColor: COLORS.ERROR,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: `${COLORS.ERROR}15`,
                    borderColor: COLORS.ERROR,
                  },
                }}
                onClick={() => {
                  const dateStr = weekStartDate ? format(weekStartDate, "yyyy-MM-dd") : "no-week";
                  const keysToRemove = [];
                  for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && (key.includes(`wmr-form-${projectId}-${dateStr}`) || key.includes(`wmr-planned-actual-${projectId}`))) {
                      keysToRemove.push(key);
                    }
                  }
                  keysToRemove.forEach(k => localStorage.removeItem(k));
                  showToast("info", "Drafts Cleared", "All weekly report drafts have been cleared.");
                  setTimeout(() => window.location.reload(), 1500);
                }}
                disabled={saving}
              >
                Clear All Drafts
              </Button>

              <Button
                variant="contained"
                size="large"
                startIcon={
                  saving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Icon name="save" />
                  )
                }
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "10px",
                  fontFamily: FONT_FAMILY,
                  backgroundColor: COLORS.PRIMARY,
                  textTransform: "none",
                  "&:disabled": {
                    backgroundColor: COLORS.LIGHT_SECONDARY,
                  },
                }}
                onClick={handleSaveWMR}
                disabled={saving}
              >
                {saving ? "Saving..." : "SUBMIT WEEKLY MONITORING REPORT"}
              </Button>
            </Box>
          </>
        )}

        {/* Date Picker Dialog */}
        <Dialog open={showDatePicker} onClose={() => setShowDatePicker(false)}>
          <DialogTitle>Select Week Start Date (Monday)</DialogTitle>
          <DialogContent>
            <DatePicker
              value={weekStartDate}
              onChange={handleWeekDateSelect}
              openTo="day"
              views={["year", "month", "day"]}
              minDate={isEngineer() ? new Date() : undefined}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  sx={{ mt: 2, display: "none" }}
                  InputProps={{
                    ...params.InputProps,
                    readOnly: true,
                  }}
                />
              )}
              shouldDisableDate={(date) => {
                // Only allow Mondays to be selected
                return !isMonday(date);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDatePicker(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Toast Component */}
        <AppToast
          open={toast.open}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={closeToast}
        />
      </Box>
    </LocalizationProvider>
  );
}

/* ================= STYLES ================= */

const labelStyle = {
  ...TYPOGRAPHY.H4,
  color: COLORS.PRIMARY,
  mb: 0.5,
  fontFamily: FONT_FAMILY,
};

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    height: "38px",
    borderRadius: "8px",
    fontFamily: FONT_FAMILY,
    "& fieldset": {
      borderColor: COLORS.LIGHT_SECONDARY,
    },
    "&:hover fieldset": {
      borderColor: COLORS.SECONDARY,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.SECONDARY,
    },
  },
  "& input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    fontSize: "13px",
  },
};

const addBtnStyle = {
  borderRadius: "7px",
  textTransform: "none",
  backgroundColor: COLORS.SECONDARY,
  color: "#fff",
  fontFamily: FONT_FAMILY,
  px: 2.5,
};

const cellStyle = {
  height: "44px",
  borderRadius: "8px",
  border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
  display: "flex",
  alignItems: "center",
  fontFamily: FONT_FAMILY,
  color: COLORS.SECONDARY,
};

const machineCellStyle = {
  height: "44px",
  borderRadius: "8px",
  border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
  display: "flex",
  alignItems: "center",
  fontFamily: FONT_FAMILY,
  color: COLORS.SECONDARY,
  paddingLeft: "12px",
};

const numberOnlyProps = {
  inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
};
